import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Container, Row, Col, Card, Form, Button, InputGroup } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { InfoCircle } from 'react-bootstrap-icons';
import CreatableSelect from 'react-select/creatable';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { AuthContext } from '../contexts/AuthContext';
import ReactMarkdown from 'react-markdown';
import './RGBMuxCalculator.css';
import { Modal } from 'react-bootstrap';
import VoltageDividerCalculatorCore from '../components/VoltageDividerCalculatorCore';

// API URL for store
const SUNTHAR_API_URL = process.env.REACT_APP_API_URL;
const MAIN_URL = process.env.REACT_APP_MAIN_URL;

const RGBMuxCalculator = () => {
  const [showVoltageDividerModal, setShowVoltageDividerModal] = useState(false);
  // Auth context for token
  const { token, role } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Authentication state
  const [authMessage, setAuthMessage] = useState('');
  const [calculatorEnabled, setCalculatorEnabled] = useState(true);
  const [crtDataLoaded, setCrtDataLoaded] = useState(false);
  
  // Circuit state
  const [circuit, setCircuit] = useState({
    // Initial values
    r1: 75, // Will be synced with r2, r3
    r2: 75,
    r3: 75,
    r4: 1000, // Will be synced with r5, r6
    r5: 1000,
    r6: 1000,
    r7: 1000, // Will be synced with r8
    r8: 1000, // Will be synced with r7
    r9: "1N4148", // Diode or shorted
    r10: '', // Set to empty string by default (open)
    r11: 1000,
    r12: 1000, // MUX transistor base resistor
    r201: 5600, // Will be synced with r202, r203
    r202: 5600,
    r203: 5600,
    r204: 820, // Will be synced with r205, r206
    r205: 820, 
    r206: 820,
    r207: 2500, // Blanking ground resistor
    r208: 2500, // Blanking inline resistor
    chassis_blanking_inductor_number: 'LN003',
    inputRedVoltage: 0.7,
  });

  // Separate state for direct injection capacitors
  const [directInjectionCaps, setDirectInjectionCaps] = useState({
    c4: 0.1, // Red input capacitor (replaces R4 in direct injection)
    c5: 0.1, // Green input capacitor (replaces R5 in direct injection)
    c6: 0.1, // Blue input capacitor (replaces R6 in direct injection)
  });

  // Model number states
  const [osdModelNumber, setOsdModelNumber] = useState('');
  const [jungleModelNumber, setJungleModelNumber] = useState('');
  const [crtModelNumber, setCrtModelNumber] = useState(null);
  const [chassisString, setChassisString] = useState(''); // Add new state for chassis string
  const [chromaVpp, setChromaVpp] = useState(''); // Add new state for chromaVpp
  
  // Pin number states for SVG display
  const [osdPins, setOsdPins] = useState(['10', '11', '12', '13']); // [R, G, B, BL]
  const [junglePins, setJunglePins] = useState(['20', '21', '22', '23']); // [R, G, B, BL]
  
  // Resistor identifier states
  const [chassisResistorIds, setChassisResistorIds] = useState(['R201', 'R202', 'R203', 'R208']); // Default identifiers
  const [chassisGroundResistorIds, setChassisGroundResistorIds] = useState(['R204', 'R205', 'R206', 'R207']); // Default ground resistor identifiers
  
  // Add new state variables for capacitor numbers and values
  const [chassisCapacitorNumbers, setChassisCapacitorNumbers] = useState('C1, C2, C3');
  const [chassisCapacitorValues, setChassisCapacitorValues] = useState('0.1');
  
  // CRT model select states
  const [suggestions, setSuggestions] = useState([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [modelInput, setModelInput] = useState('');
  const [selectedCrtModel, setSelectedCrtModel] = useState(null);
  
  // CRT RGB modifiable status
  const [crtRgbModifiable, setCrtRgbModifiable] = useState(null);

  // CRT mod boards
  const [crtModBoards, setCrtModBoards] = useState([]);

  // CRT RGB modification notes
  const [crtRgbModNotes, setCrtRgbModNotes] = useState('');

  // CRT RGB modification link
  const [crtRgbModLink, setCrtRgbModLink] = useState('');

  // Vpp input voltage
  const [vppInputVoltage, setVppInputVoltage] = useState(0.7);
  
  // Chassis RGB diodes toggle state
  const [showChassisRGBDiodes, setShowChassisRGBDiodes] = useState(false);
  const [showChassisBlankingDiode, setShowChassisBlankingDiode] = useState(false);
  const [showChassisBlankingAddDiodeLabel, setShowChassisBlankingAddDiodeLabel] = useState(false);
  const [showChassisRGBAddDiodesLabel, setShowChassisRGBAddDiodesLabel] = useState(false);
  const [showMuxBlankingDiode, setShowMuxBlankingDiode] = useState(true);
  const [showMuxTransistor, setShowMuxTransistor] = useState(false);
  const [showChassisStereo, setShowChassisStereo] = useState(false);
  const [showDirectInjection, setShowDirectInjection] = useState(false);
  const [showChassisBlankingInductor, setShowChassisBlankingInductor] = useState(false);
  const [showChassisBlankingLine, setShowChassisBlankingLine] = useState(true);
  const [showChangeCapsLabel, setShowChangeCapsLabel] = useState(false);
  const [blankingDiodeBeforeR207, setBlankingDiodeBeforeR207] = useState(false);
  
  // Formula explanation card collapsed state
  const [formulaCardCollapsed, setFormulaCardCollapsed] = useState(true);
  
  // Voltage calculation card collapsed state
  const [voltageCardCollapsed, setVoltageCardCollapsed] = useState(true);
  
  // Advanced configuration section collapsed state
  const [advancedConfigCollapsed, setAdvancedConfigCollapsed] = useState(true);
  
  // Which parameter to calculate mode
  const [calculateMode, setCalculateMode] = useState('outputVoltage'); // 'rgbResistor' or 'outputVoltage'
  
  const handleLogin = () => {
    localStorage.setItem('postLoginRedirect', '/rgb-mux-calculator');
    navigate('/login');
  };

  // Format resistor value for display
  const formatResistorValue = (value) => {
    // Return "open" for null, undefined, or empty string
    if (value === null || value === undefined || value === '') {
      return "open";
    }
    
    // Return "short" for zero values
    if (value === 0) {
      return "short";
    }
    
    if (value >= 1000) {
      return (value / 1000).toFixed(1) + ' kΩ';
    }
    return value + ' Ω';
  };

  // Handle input changes for circuit values
  const handleInputChange = (e) => {
    const name = e.target.name;
    let value = e.target.value;

    // Keep empty strings as empty instead of converting to 0
    // This allows blank values from CRT data to remain blank
    if (value === '') {
      value = '';
    } else if (!isNaN(value)) {
      value = parseFloat(value);
    }

    setCircuit(prev => {
      let newCircuit = { ...prev };

      if (name === 'inputR1R2R3') {
        // Set R1, R2, R3 to the same value
        newCircuit.r1 = value;
        newCircuit.r2 = value;
        newCircuit.r3 = value;
      } else if (name === 'inputR4R5R6') {
        if (showDirectInjection) {
          // In Direct Injection mode, update capacitor values
          setDirectInjectionCaps(prev => ({
            ...prev,
            c4: value,
            c5: value,
            c6: value
          }));
          // Don't update circuit resistor values in Direct Injection mode
          return newCircuit;
        } else {
          // In MUX mode, update resistor values
          newCircuit.r4 = value;
          newCircuit.r5 = value;
          newCircuit.r6 = value;
        }
      } else if (name === 'inputR7R8') {
        // Set R7, R8 to the same value
        newCircuit.r7 = value;
        newCircuit.r8 = value;
      } else if (name === 'inputR201R202R203') {
        // Set R201, R202, R203 to the same value
        newCircuit.r201 = value;
        newCircuit.r202 = value;
        newCircuit.r203 = value;
      } else if (name === 'inputR204R205R206') {
        // Set R204, R205, R206 to the same value
        newCircuit.r204 = value;
        newCircuit.r205 = value;
        newCircuit.r206 = value;
      } else {
        // Single resistor value
        newCircuit[name] = value;
      }

      return newCircuit;
    });
  };

  // Handle Vpp input voltage change
  const handleVppInputVoltageChange = (e) => {
    setVppInputVoltage(parseFloat(e.target.value));
  };

  // Handle toggle for RGB diodes
  const handleChassisRGBDiodesToggle = (e) => {
    setShowChassisRGBDiodes(e.target.checked);
  };
  
  // Handle toggle for blanking diode
  const handleChassisBlankingDiodeToggle = (e) => {
    setShowChassisBlankingDiode(e.target.checked);
  };
  
  // Handle toggle for blanking input diode
  const handleMuxBlankingDiodeToggle = (e) => {
    setShowMuxBlankingDiode(e.target.checked);
  };
  
  // Handle toggle for direct injection
  const handleDirectInjectionToggle = (e) => {
    const isDirectInjectionEnabled = e.target.checked;
    setShowDirectInjection(isDirectInjectionEnabled);
    
    // If direct injection is enabled, disable chassis RGB diodes and enable blanking line
    if (isDirectInjectionEnabled) {
      setShowChassisRGBDiodes(false);
      setShowChassisBlankingLine(true);  // Enable blanking line when Direct Injection is enabled
    } else {
      // If direct injection is disabled, also disable chassis blanking line
      setShowChassisBlankingLine(false);
    }
  };
  
  // Handle toggle for chassis blanking line
  const handleChassisBlankingLineToggle = (e) => {
    setShowChassisBlankingLine(e.target.checked);
  };
  
  // Handle toggle for change caps label
  const handleChangeCapsLabelToggle = (e) => {
    setShowChangeCapsLabel(e.target.checked);
  };
  
  // Handle toggle for blanking diode position
  const handleBlankingDiodePositionToggle = (e) => {
    setBlankingDiodeBeforeR207(e.target.checked);
  };
  
  // Handle toggle for MUX transistor
  const handleMuxTransistorToggle = (e) => {
    setShowMuxTransistor(e.target.checked);
  };
  
  // Handle calculation mode change
  const handleCalculateModeChange = (e) => {
    setCalculateMode(e.target.value);
  };

  // Calculate the circuit values
  const calculateCircuit = () => {
    // Helper function to convert empty values to 0 for calculations
    const getValue = (value) => (value === '' || value === null || value === undefined) ? 0 : parseFloat(value) || 0;
    
    const vChromaPeak = vppInputVoltage;
    const rOsdInlineResistor = getValue(circuit.r201);
    const vOsd = 5.0; // Typical OSD voltage
    const vDiode = showChassisRGBDiodes ? 0.7 : 0; // Diode voltage drop if enabled
    const terminationResistor = getValue(circuit.r1); // Use the dynamic termination resistor value
    
    if (calculateMode === 'rgbResistor') {
      // Calculate RGB input resistor value using the formula:
      // R_rgb.external.resistor = (V_chroma.peak * (R_osd.inline.resistor + termination_resistor) - termination_resistor * (V_osd - V_diode)) / (V_osd - V_diode - V_chroma.peak)
      let rgbResistorValue = 0;
      
      try {
        const numerator = (vChromaPeak * (rOsdInlineResistor + terminationResistor)) - (terminationResistor * (vOsd - vDiode));
        const denominator = (vOsd - vDiode - vChromaPeak);
        
        // Check for division by zero
        if (denominator !== 0) {
          rgbResistorValue = numerator / denominator;
          // Round to nearest typical resistor value
          rgbResistorValue = Math.max(0, Math.round(rgbResistorValue));
        }
      } catch (error) {
        console.error("Error calculating resistor value:", error);
        rgbResistorValue = 0;
      }
      
      // Update the circuit with calculated value
      setCircuit(prevCircuit => ({
        ...prevCircuit,
        r4: rgbResistorValue,
        r5: rgbResistorValue,
        r6: rgbResistorValue,
        inputRedVoltage: vppInputVoltage,
        inputGreenVoltage: vppInputVoltage,
        inputBlueVoltage: vppInputVoltage
      }));
    } else if (calculateMode === 'outputVoltage') {
      // Calculate output voltage using the RGB input resistor, OSD inline resistor, and termination resistor
      // Vout = (vOsd - vDiode) * (rgbInputResistor + terminationResistor) / (rOsdInlineResistor + rgbInputResistor + terminationResistor))
      
      const rgbInputResistor = getValue(circuit.r4);
      let outputVoltage = 0;
      
      try {
        // Apply the formula: Vout = (vOsd - vDiode) * (rgbInputResistor + terminationResistor) / (rOsdInlineResistor + rgbInputResistor + terminationResistor))
        const vSourceWithDiode = vOsd - (showChassisRGBDiodes ? vDiode : 0);
        const numerator = vSourceWithDiode * (rgbInputResistor + terminationResistor);
        const denominator = rOsdInlineResistor + rgbInputResistor + terminationResistor;
        
        outputVoltage = numerator / denominator;
        
        // Limit output voltage to reasonable range
        outputVoltage = Math.min(5, Math.max(0, outputVoltage));
      } catch (error) {
        console.error("Error calculating output voltage:", error);
        outputVoltage = 0;
      }
      
      // Update the circuit with calculated value
      setVppInputVoltage(parseFloat(outputVoltage.toFixed(2)));
    }
  };

  // Run calculation when values change
  useEffect(() => {
    calculateCircuit();
  }, [
    circuit.r1, circuit.r2, circuit.r3, circuit.r4, circuit.r5, circuit.r6,
    circuit.r7, circuit.r8, circuit.r9, circuit.r10, circuit.r11, circuit.r12,
    circuit.r201, circuit.r202, circuit.r203, circuit.r204, circuit.r205, circuit.r206,
    calculateMode, showChassisRGBDiodes, showChassisBlankingDiode, showMuxBlankingDiode, showMuxTransistor, showDirectInjection, showChassisBlankingLine, showChangeCapsLabel, blankingDiodeBeforeR207, vppInputVoltage,
    chassisCapacitorNumbers, chassisCapacitorValues, directInjectionCaps
  ]);
  
  // Render the SVG diagram for the RGB MUX circuit
  const renderRGBMuxSVG = () => {
    // Format resistor values for display
    const r1Value = formatResistorValue(circuit.r1);
    const r4Value = formatResistorValue(circuit.r4);
    const r7Value = formatResistorValue(circuit.r7);
    const r9Value = showMuxBlankingDiode ? "1N4148" : "shorted";
    const r10Value = (circuit.r10 === null || circuit.r10 === undefined || circuit.r10 === '') ? "open" : formatResistorValue(circuit.r10);
    const r11Value = formatResistorValue(circuit.r11);
    const r12Value = formatResistorValue(circuit.r12);
    
    // Parse capacitor information
    const capacitorNumbersArray = chassisCapacitorNumbers.split(',').map(num => num.trim());
    const capacitorValue = chassisCapacitorValues; // Assume single value for all capacitors
    
    // Format capacitor value for display
    const formatCapacitorValue = (value) => {
      if (!value || value === '' || isNaN(parseFloat(value))) {
        return "N/A";
      }
      const numValue = parseFloat(value);
      if (numValue < 1) {
        return `${numValue} μF`;
      } else {
        return `${numValue} μF`;
      }
    };
    
    const capacitorValueFormatted = formatCapacitorValue(capacitorValue);
    
    // Get capacitor identifiers with fallbacks
    const c1Id = capacitorNumbersArray[0] || 'C1';
    const c2Id = capacitorNumbersArray[1] || 'C2';
    const c3Id = capacitorNumbersArray[2] || 'C3';
    
    return (
      <svg width="1400" height="100%" viewBox="0 0 1400 1080" fill="none" 
        xmlns="http://www.w3.org/2000/svg" 
        xmlnsXlink="http://www.w3.org/1999/xlink"
        className="svg-diagram"
        style={{ maxWidth: "100%", height: "auto" }}>
        <g id="RGB_MUX_DIAGRAM" clipPath="url(#clip0_1_731)">
          <rect width="1400" height="1080" fill="white" />
          <g id="RGB_DIAGRAM">
            {(!showDirectInjection || showChassisBlankingLine) && (
            <g id="BLANKING_LINE">
              <path id="R208_TO_DIODE" d="M128 316V557H483" stroke="#B45F06" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round"/>
              <path id="DIODE_JUNGLE" d="M627 557H483" stroke="#B45F06" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
              {circuit.r207 && ( 
                <g id="R207_SECTION">
                  <path id="TO_R207" d="M128 415H98V443" stroke="#B45F06" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                  <text id="R207_TEXT" transform="translate(66 495) rotate(-90)" fill="black" xmlSpace="preserve" className="svg-text svg-text-small">
                    <tspan x="0" y="11.8636">{chassisGroundResistorIds[3]} - {formatResistorValue(circuit.r207)}</tspan>
                  </text>
                  <g id="R207_TO_GROUND">
                    <path id="Vector" d="M98.7184 491V511.409" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                    <path id="Vector_2" d="M85.624 511.425H111.813" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                    <path id="Vector_3" d="M89.503 516.362H106.962" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                    <path id="Vector_4" d="M94.3478 521.298H103.077" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                  </g>
                  <path id="R207" d="M107.366 479.275L94.9443 485.486L98.9717 487.5L98.9717 491.973L89.5771 487.275L86 485.486L89.5771 483.697L101.999 477.486L89.5771 471.275L86 469.486L89.5771 467.697L101.999 461.486L89.5771 455.275L86 453.486L89.5771 451.697L101.999 445.486L97.9717 443.473L97.9717 439L107.366 443.697L110.943 445.486L107.366 447.275L94.9443 453.486L107.366 459.697L110.943 461.486L107.366 463.275L94.9443 469.486L107.366 475.697L110.943 477.486L107.366 479.275Z" fill="black" />
                </g>
              )}
              {parseFloat(circuit.r208) === 0 ? (
                <>
                  <path id="OSD_TO_R208" d="M128 270L128 210.5" stroke="#B45F06" strokeWidth="8" />
                  <path id="R208_SHORTED" d="M128 316L128 270" stroke="#B45F06" strokeWidth="8"/>
                </>
              ) : (
                <>
                  <text id="R208_TEXT" transform="translate(99 308) rotate(-90)" fill="black" xmlSpace="preserve" className="svg-text svg-text-small">
                    <tspan x="0" y="11.8636">{chassisResistorIds[3]} - {formatResistorValue(circuit.r208)}</tspan>
                  </text>
                  <path id="R208" d="M137.366 307.275L124.944 313.486L128.972 315.5L128.972 319.973L119.577 315.275L116 313.486L119.577 311.697L131.999 305.486L119.577 299.275L116 297.486L119.577 295.697L131.999 289.486L119.577 283.275L116 281.486L119.577 279.697L131.999 273.486L127.972 271.473L127.972 267L137.366 271.697L140.943 273.486L137.366 275.275L124.944 281.486L137.366 287.697L140.943 289.486L137.366 291.275L124.944 297.486L137.366 303.697L140.943 305.486L137.366 307.275Z" fill="black" />
                  <path id="OSD_TO_R208" d="M128 270L128 210.5" stroke="#B45F06" strokeWidth="8" />
                </>
              )}
              {showChassisBlankingDiode && !blankingDiodeBeforeR207 && (
              <g id="BLANKING_DIODE_AFTER_R207">
                <path id="Vector_5" d="M448 556.3H492.094" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="Vector_6" d="M481.362 542V569.59" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="Vector_7" fillRule="evenodd" clipRule="evenodd" d="M458.299 542.221L481.795 555.796L458.299 569.371V542.221Z" fill="black" />
                {showChassisBlankingAddDiodeLabel && (
                <>
                <path id="ADD_BLANKING_DIODE_ARROW" d="M454 572L442.507 573.114L449.218 582.51L454 572ZM433 587L433.581 587.814L447.258 578.045L446.676 577.231L446.095 576.417L432.419 586.186L433 587Z" fill="black"/>
                <text id="ADD_BLANKING_DIODE_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-medium"><tspan x="352.039" y="597.318">add diode</tspan></text>
                </>
                )}
              </g>
              )}
              {showChassisBlankingDiode && blankingDiodeBeforeR207 && (
              <>
                <g id="BLANKING_DIODE_BEFORE_R207">
                  <path id="Vector_8" d="M128.291 347L128.291 391.094" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                  <path id="Vector_9" d="M142.591 380.362L115 380.362" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                  <path id="Vector_10" fillRule="evenodd" clipRule="evenodd" d="M142.369 357.299L128.794 380.795L115.22 357.299L142.369 357.299Z" fill="black" />
                </g>
                {showChassisBlankingAddDiodeLabel && (
                <>
                <path id="ADD_BLANKING_DIODE_ARROW" d="M107 369L97 363.226V374.774L107 369ZM76.5 369V370H98V369V368H76.5V369Z" fill="black"/>
                <text id="ADD_BLANKING_DIODE_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-medium">
                  <tspan x="36.8047" y="366.318">add &#x2028;</tspan><tspan x="32.2344" y="385.318">diode</tspan>
                </text>
                </>
                )}
              </>
              )}
            </g>
            )}
            {showChassisBlankingInductor ? (
              <>
                <text id="INDUCTOR_TEXT" transform="translate(595 423) rotate(-90)" fill="black" xmlSpace="preserve" className="svg-text svg-text-small">
                  <tspan x="0" y="11.8636">{circuit.chassis_blanking_inductor_number || 'LN003'}</tspan>
                </text>
                <path id="BLANKING_INDUCTOR" d="M615 390.236C615 385.65 618.667 383.577 621.388 382.702C622.014 382.501 622.636 382.347 623.223 382.227L623.223 376L627.223 376L627.223 385.686L627 385.686L627.001 385.802L626.992 385.802C626.98 385.802 626.96 385.802 626.931 385.803C626.873 385.804 626.781 385.807 626.662 385.813C626.423 385.823 626.073 385.845 625.65 385.891C624.795 385.982 623.69 386.163 622.612 386.51C620.333 387.243 619 388.389 619 390.236C619 392.084 620.333 393.23 622.612 393.963C623.69 394.309 624.795 394.489 625.65 394.581C626.073 394.626 626.423 394.649 626.662 394.66C626.781 394.666 626.873 394.669 626.931 394.67C626.96 394.671 626.98 394.671 626.992 394.671L627.001 394.671L627 396.671L627.001 398.671L626.992 398.671C626.98 398.671 626.96 398.671 626.931 398.672C626.873 398.673 626.781 398.675 626.662 398.681C626.423 398.692 626.073 398.714 625.65 398.76C624.795 398.851 623.69 399.032 622.612 399.379C620.333 400.112 619 401.258 619 403.105C619 404.952 620.333 406.098 622.612 406.831C623.69 407.178 624.795 407.358 625.65 407.45C626.073 407.496 626.423 407.518 626.662 407.529C626.781 407.535 626.873 407.537 626.931 407.538C626.96 407.539 626.98 407.539 626.992 407.539L627.001 407.539L627 409.539L627.001 411.539L626.992 411.539C626.98 411.539 626.96 411.54 626.931 411.541C626.873 411.542 626.781 411.544 626.662 411.55C626.423 411.561 626.073 411.584 625.65 411.629C624.795 411.721 623.69 411.902 622.612 412.248C620.333 412.981 619 414.127 619 415.974C619 417.821 620.333 418.967 622.612 419.7C623.69 420.047 624.795 420.228 625.65 420.319C626.008 420.358 626.314 420.378 626.544 420.391L627.223 420.391L627.223 429L623.223 429L623.223 423.984C622.636 423.864 622.015 423.709 621.388 423.508C618.667 422.633 615 420.561 615 415.974C615 412.721 616.845 410.733 618.889 409.54C616.845 408.347 615 406.359 615 403.105C615 399.852 616.845 397.864 618.889 396.671C616.845 395.478 615 393.49 615 390.236Z" fill="black" />
              </>
            ) : (
              <path id="BLANKING_NO_INDUCTOR" d="M625 376V429" stroke="#B45F06" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
            )}

            {r10Value !== "open" && (
            <g id="R10_LINES">
              <path id="D9_TO_R10" d="M916.64 582.858H899.066" stroke="#B45F06" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
              <path id="R10_TO_GROUND" d="M852 583H795V603" stroke="black" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
              <text id="R10_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-small">
                <tspan x="925" y="586.864">R10</tspan>
              </text>
              <path id="R10" d="M861 592.366L854.789 579.944L851.578 586.366L848 584.577L853 574.577L854.789 571L856.578 574.577L862.789 586.999L869 574.577L870.789 571L872.578 574.577L878.789 586.999L885 574.577L886.789 571L888.578 574.577L894.789 586.999L898 580.577L901.578 582.366L896.578 592.366L894.789 595.943L893 592.366L886.789 579.944L880.578 592.366L878.789 595.943L877 592.366L870.789 579.944L864.578 592.366L862.789 595.943L861 592.366Z" fill="black" />
            </g>
            )}
            <g id="RGB_LINES">

              {showDirectInjection ? (
              <g id="DIRECT">
                <path id="B_TO_JUNGLE" d="M793 210V459" stroke="#0000FF" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="G_TOJUNGLE" d="M737 210L737 489" stroke="#00FF00" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="R_TO_JUNGLE" d="M681 210L681 523" stroke="#FF0000" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                <g id="C6">
                  <path id="Vector_11" d="M1247.99 618V635.354" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                  <path id="Vector_12" d="M1260.99 634.997L1235 634.924" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                  <path id="Vector_13" d="M1260.99 642.997L1235 642.924" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                  <path id="Vector_14" d="M1247.99 643V660.354" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                </g>
                <g id="C5">
                  <path id="Vector_15" d="M1114.99 618V635.354" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                  <path id="Vector_16" d="M1127.99 634.997L1102 634.924" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                  <path id="Vector_17" d="M1127.99 642.997L1102 642.924" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                  <path id="Vector_18" d="M1114.99 643V660.354" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                </g>
                <g id="C4">
                  <path id="Vector_19" d="M981.989 619V636.354" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                  <path id="Vector_20" d="M994.989 635.997L969 635.924" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                  <path id="Vector_21" d="M994.989 643.997L969 643.924" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                  <path id="Vector_22" d="M981.989 644V661.354" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                </g>
              </g>
              ):(
              <g id="MUX">
                <path id="B_TO_JUNGLE_2" d="M793.144 210.155L793.001 306" stroke="#0000FF" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="G_TO_JUNGLE" d="M737 210V306" stroke="#00FF00" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="R_TO_JUNGLE_2" d="M681 210L681 306" stroke="#FF0000" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                <g id="RGB_INLINE_CAPS">
                  <text id="B_C3_TEXT" transform="translate(761 354) rotate(-90)" fill="black" xmlSpace="preserve" className="svg-text svg-text-small">
                    <tspan x="0" y="11.8636">{c3Id} - {capacitorValueFormatted}</tspan>
                  </text>
                  <g id="B_C3">
                    <path id="Vector_23" d="M792.989 306V323.354" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                    <path id="Vector_24" d="M805.989 322.997L780 322.924" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                    <path id="Vector_25" d="M805.989 330.997L780 330.924" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                    <path id="Vector_26" d="M792.989 331V348.354" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                  </g>
                  <text id="G_C2_TEXT" transform="translate(704 354) rotate(-90)" fill="black" xmlSpace="preserve" className="svg-text svg-text-small">
                    <tspan x="0" y="11.8636">{c2Id} - {capacitorValueFormatted}</tspan>
                  </text>
                  <g id="G_C2">
                    <path id="Vector_27" d="M736.989 306V323.354" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                    <path id="Vector_28" d="M749.989 322.997L724 322.924" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                    <path id="Vector_29" d="M749.989 330.997L724 330.924" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                    <path id="Vector_30" d="M736.989 331V348.354" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                  </g>
                  <text id="R_C1_TEXT" transform="translate(646 354) rotate(-90)" fill="black" xmlSpace="preserve" className="svg-text svg-text-small">
                    <tspan x="0" y="11.8636">{c1Id} - {capacitorValueFormatted}</tspan>
                  </text>
                  <g id="R_C1">
                    <path id="Vector_31" d="M680.989 306V323.354" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                    <path id="Vector_32" d="M693.989 322.997L668 322.924" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                    <path id="Vector_33" d="M693.989 330.997L668 330.924" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                    <path id="Vector_34" d="M680.989 331V348.354" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                  </g>
                  {showChangeCapsLabel && (
                  <>
                  <text id="CHANGE_CAPS_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-medium">
                    <tspan x="510.305" y="245.318">change caps</tspan>
                  </text>
                  <path id="CHANGE_CAPS_ARROW" d="M575.122 277.067L575.919 276.463L575.919 276.463L575.122 277.067ZM619.463 310.045L619.812 309.108L619.463 310.045ZM643.5 319L636.145 310.099L632.114 320.919L643.5 319ZM559 255.793L558.203 256.397L574.325 277.671L575.122 277.067L575.919 276.463L559.797 255.189L559 255.793ZM619.463 310.045L619.114 310.982L634.717 316.795L635.066 315.858L635.415 314.921L619.812 309.108L619.463 310.045ZM575.122 277.067L574.325 277.671C585.79 292.801 601.325 304.355 619.114 310.982L619.463 310.045L619.812 309.108C602.379 302.613 587.155 291.291 575.919 276.463L575.122 277.067Z" fill="black"/>
                  </>
                  )}
                </g>
                <path id="R6" d="M1256.37 652.578L1243.94 658.789L1250.37 662L1248.58 665.578L1238.58 660.578L1235 658.789L1238.58 657L1251 650.789L1238.58 644.578L1235 642.789L1238.58 641L1251 634.789L1238.58 628.578L1235 626.789L1238.58 625L1251 618.789L1244.58 615.578L1246.37 612L1256.37 617L1259.94 618.789L1256.37 620.578L1243.94 626.789L1256.37 633L1259.94 634.789L1256.37 636.578L1243.94 642.789L1256.37 649L1259.94 650.789L1256.37 652.578Z" fill="black" />
                <path id="R5" d="M1124.37 652.578L1111.94 658.789L1118.37 662L1116.58 665.578L1106.58 660.578L1103 658.789L1106.58 657L1119 650.789L1106.58 644.578L1103 642.789L1106.58 641L1119 634.789L1106.58 628.578L1103 626.789L1106.58 625L1119 618.789L1112.58 615.578L1114.37 612L1124.37 617L1127.94 618.789L1124.37 620.578L1111.94 626.789L1124.37 633L1127.94 634.789L1124.37 636.578L1111.94 642.789L1124.37 649L1127.94 650.789L1124.37 652.578Z" fill="black" />
                <path id="R4" d="M991.366 652.578L978.944 658.789L985.366 662L983.577 665.578L973.577 660.578L970 658.789L973.577 657L985.999 650.789L973.577 644.578L970 642.789L973.577 641L985.999 634.789L973.577 628.578L970 626.789L973.577 625L985.999 618.789L979.577 615.578L981.366 612L991.366 617L994.943 618.789L991.366 620.578L978.944 626.789L991.366 633L994.943 634.789L991.366 636.578L978.944 642.789L991.366 649L994.943 650.789L991.366 652.578Z" fill="black" />
                <path id="B_TO_C3" d="M793 348V459" stroke="#0000FF" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="G_TO_C2" d="M737 348L737 489" stroke="#00FF00" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="R_TO_C1" d="M681 348L681 523" stroke="#FF0000" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="B_TO_DIODE" d="M297 316V455H789" stroke="#0000FF" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="G_TO_DIODE" d="M242 316V485H733" stroke="#00FF00" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="R_TO_DIODE" d="M186 316V523H677" stroke="#FF0000" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                {(showChassisRGBDiodes && showChassisRGBAddDiodesLabel) && (
                <>
                  <text id="ADD_DIODES_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-medium">
                    <tspan x="431.359" y="403.318">add diodes</tspan>
                  </text>
                  <path id="ADD_DIODES_ARROW" d="M472 433L477.774 423L466.226 423L472 433ZM472 408L471 408L471 424L472 424L473 424L473 408L472 408Z" fill="black" />
                </>
                )}
                {showChassisRGBDiodes && (
                <g id="RGB_DIODES">
                  <g id="B_INLINE_DIODE">
                    <path id="Vector_35" d="M448 455.3H492.094" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                    <path id="Vector_36" d="M481.362 441V468.591" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                    <path id="Vector_37" fillRule="evenodd" clipRule="evenodd" d="M458.299 441.221L481.795 454.796L458.299 468.371V441.221Z" fill="black" />
                  </g>
                  <g id="G_INLINE_DIODE">
                    <path id="Vector_38" d="M448 485.3H492.094" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                    <path id="Vector_39" d="M481.362 471V498.59" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                    <path id="Vector_40" fillRule="evenodd" clipRule="evenodd" d="M458.299 471.221L481.795 484.796L458.299 498.371V471.221Z" fill="black" />
                  </g>
                  <g id="R_INLINE_DIODE">
                    <path id="Vector_41" d="M448 523.3H492.094" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                    <path id="Vector_42" d="M481.362 509V536.59" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                    <path id="Vector_43" fillRule="evenodd" clipRule="evenodd" d="M458.299 509.221L481.795 522.796L458.299 536.371V509.221Z" fill="black" />
                  </g>

                </g>
                )}
                <g id="CHASSIS_GROUND_RESISTORS">
                  <g id="B_R206_GROUND">
                    <path id="Vector_44" d="M327.094 408V428.409" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                    <path id="Vector_45" d="M314 428.425H340.189" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                    <path id="Vector_46" d="M317.879 433.362H335.338" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                    <path id="Vector_47" d="M322.724 438.298H331.454" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                  </g>
                  <g id="G_R205_GROUND">
                    <path id="Vector_48" d="M272.094 408V428.409" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                    <path id="Vector_49" d="M259 428.425H285.189" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                    <path id="Vector_50" d="M262.879 433.362H280.338" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                    <path id="Vector_51" d="M267.724 438.298H276.454" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                  </g>
                  <g id="R_R204_GROUND">
                    <path id="Vector_52" d="M215.094 408V428.409" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                    <path id="Vector_53" d="M202 428.425H228.189" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                    <path id="Vector_54" d="M205.879 433.362H223.338" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                    <path id="Vector_55" d="M210.724 438.298H219.454" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                  </g>
                  <path id="B_CHASSIS_GROUND" d="M300.186 333.663H327.777V361.296" stroke="#0000FF" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                  <path id="G_CHASSIS_GROUND" d="M244 334H273V361" stroke="#00FF00" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                  <path id="R_CHASSIS_GROUND" d="M185 334H217V361" stroke="#FF0000" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                  <text id="R206_TEXT" transform="translate(301 420) rotate(-90)" fill="black" xmlSpace="preserve" className="svg-text svg-text-small">
                    <tspan x="0" y="11.8636">{chassisGroundResistorIds[2]} - {formatResistorValue(circuit.r206)}</tspan>
                  </text>
                  <path id="R206" d="M337.366 398.275L324.944 404.486L328.972 406.5L328.972 410.973L319.577 406.275L316 404.486L319.577 402.697L331.999 396.486L319.577 390.275L316 388.486L319.577 386.697L331.999 380.486L319.577 374.275L316 372.486L319.577 370.697L331.999 364.486L327.972 362.473L327.972 358L337.366 362.697L340.943 364.486L337.366 366.275L324.944 372.486L337.366 378.697L340.943 380.486L337.366 382.275L324.944 388.486L337.366 394.697L340.943 396.486L337.366 398.275Z" fill="black" />
                  <text id="R205_TEXT" transform="translate(246 420) rotate(-90)" fill="black" xmlSpace="preserve" className="svg-text svg-text-small">
                    <tspan x="0" y="11.8636">{chassisGroundResistorIds[1]} - {formatResistorValue(circuit.r205)}</tspan>
                  </text>
                  <path id="R205" d="M282.366 397.275L269.944 403.486L273.972 405.5L273.972 409.973L264.577 405.275L261 403.486L264.577 401.697L276.999 395.486L264.577 389.275L261 387.486L264.577 385.697L276.999 379.486L264.577 373.275L261 371.486L264.577 369.697L276.999 363.486L272.972 361.473L272.972 357L282.366 361.697L285.943 363.486L282.366 365.275L269.944 371.486L282.366 377.697L285.943 379.486L282.366 381.275L269.944 387.486L282.366 393.697L285.943 395.486L282.366 397.275Z" fill="black" />
                  <text id="R204_TEXT" transform="translate(190 420) rotate(-90)" fill="black" xmlSpace="preserve" className="svg-text svg-text-small">
                    <tspan x="0" y="11.8636">{chassisGroundResistorIds[0]} - {formatResistorValue(circuit.r204)}</tspan>
                  </text>
                  <path id="R204" d="M225.366 397.275L212.944 403.486L216.972 405.5L216.972 409.973L207.577 405.275L204 403.486L207.577 401.697L219.999 395.486L207.577 389.275L204 387.486L207.577 385.697L219.999 379.486L207.577 373.275L204 371.486L207.577 369.697L219.999 363.486L215.972 361.473L215.972 357L225.366 361.697L228.943 363.486L225.366 365.275L212.944 371.486L225.366 377.697L228.943 379.486L225.366 381.275L212.944 387.486L225.366 393.697L228.943 395.486L225.366 397.275Z" fill="black" />
                  <text id="REMOVE_RESISTORS_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-medium">
                    <tspan x="391.18" y="286.318">remove resistors</tspan>
                    <tspan x="390.133" y="305.318">{chassisGroundResistorIds[0]},{chassisGroundResistorIds[1]},{chassisGroundResistorIds[2]}</tspan>
                  </text>
                  <path id="REMOVE_RESISTOR_ARROW" d="M360.524 387.5L360.524 388.5L360.524 387.5ZM354 387.5L364 393.274L364 381.727L354 387.5ZM447 317L446.021 316.799C437.674 357.378 401.953 386.5 360.524 386.5L360.524 387.5L360.524 388.5C402.902 388.5 439.442 358.711 447.98 317.201L447 317Z" fill="black" />
                </g>
                <text id="R203_TEXT" transform="translate(270 308) rotate(-90)" fill="black" xmlSpace="preserve" className="svg-text svg-text-small">
                  <tspan x="0" y="11.8636">{chassisResistorIds[2]} - {formatResistorValue(circuit.r203)}</tspan>
                </text>
                <path id="R203" d="M305.366 307.275L292.944 313.486L296.972 315.5L296.972 319.973L287.577 315.275L284 313.486L287.577 311.697L299.999 305.486L287.577 299.275L284 297.486L287.577 295.697L299.999 289.486L287.577 283.275L284 281.486L287.577 279.697L299.999 273.486L295.972 271.473L295.972 267L305.366 271.697L308.943 273.486L305.366 275.275L292.944 281.486L305.366 287.697L308.943 289.486L305.366 291.275L292.944 297.486L305.366 303.697L308.943 305.486L305.366 307.275Z" fill="black" />
                <text id="R202_TEXT" transform="translate(213 307) rotate(-90)" fill="black" xmlSpace="preserve" className="svg-text svg-text-small">
                  <tspan x="0" y="11.8636">{chassisResistorIds[1]} - {formatResistorValue(circuit.r202)}</tspan>
                </text>
                <path id="R202" d="M251.423 307.275L239.001 313.486L243.028 315.5L243.028 319.973L233.634 315.275L230.057 313.486L233.634 311.697L246.056 305.486L233.634 299.275L230.057 297.486L233.634 295.697L246.056 289.486L233.634 283.275L230.057 281.486L233.634 279.697L246.056 273.486L242.028 271.473L242.028 267L251.423 271.697L255 273.486L251.423 275.275L239.001 281.486L251.423 287.697L255 289.486L251.423 291.275L239.001 297.486L251.423 303.697L255 305.486L251.423 307.275Z" fill="black" />
                <text id="R201_TEXT" transform="translate(157 306) rotate(-90)" fill="black" xmlSpace="preserve" className="svg-text svg-text-small">
                  <tspan x="0" y="11.8636">{chassisResistorIds[0]} - {formatResistorValue(circuit.r201)}</tspan>
                </text>
                <path id="R201" d="M193.423 307.275L181.001 313.486L185.028 315.5L185.028 319.973L175.634 315.275L172.057 313.486L175.634 311.697L188.056 305.486L175.634 299.275L172.057 297.486L175.634 295.697L188.056 289.486L175.634 283.275L172.057 281.486L175.634 279.697L188.056 273.486L184.028 271.473L184.028 267L193.423 271.697L197 273.486L193.423 275.275L181.001 281.486L193.423 287.697L197 289.486L193.423 291.275L181.001 297.486L193.423 303.697L197 305.486L193.423 307.275Z" fill="black" />
                <path id="B_FROM_OSD" d="M297 210L297 269" stroke="#0000FF" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="G_FROM_OSD" d="M241 210L241 269" stroke="#00FF00" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="R_FROM_OSD" d="M185.144 210.155V270.407" stroke="#FF0000" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
              </g>
              )}
              
              <g id="COMMON">
                <path id="BLANKING_TO_INDUCTOR" d="M625 429V557H848" stroke="#B45F06" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="B_TO_R3" d="M1248 676L1281 660" stroke="#0000FF" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="G_TO_R2" d="M1115 676L1147 660" stroke="#00FF00" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="R_TO_R1" d="M983 676L1015 660" stroke="#FF0000" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="B_R6_TO_JUNGLE" d="M1248 618V455H789" stroke="#0000FF" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="G_R5_TO_JUNGLE" d="M1115 618V485H733" stroke="#00FF00" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="R_R4_TO_JUNGLE" d="M982 620V523H677" stroke="#FF0000" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="B_TO_R6" d="M1248 679V661" stroke="#0000FF" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="G_TO_R5" d="M1115 679V661" stroke="#00FF00" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="R_TO_R4" d="M982 679V661" stroke="#FF0000" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="D9_TO_R11" d="M916 611V557H900" stroke="#B45F06" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="PIN16_TO_D9" d="M916 679L916 654" stroke="#B45F06" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                <text id="R6_TEXT" transform="translate(1217 643) rotate(-90)" fill="black" xmlSpace="preserve" className="svg-text svg-text-small">
                  <tspan x="0" y="11.8636">R6</tspan>
                </text>
                <text id="R5_TEXT" transform="translate(1088 643) rotate(-90)" fill="black" xmlSpace="preserve" className="svg-text svg-text-small">
                  <tspan x="0" y="11.8636">R5</tspan>
                </text>
                <text id="R4_TEXT" transform="translate(952 643) rotate(-90)" fill="black" xmlSpace="preserve" className="svg-text svg-text-small">
                  <tspan x="0" y="11.8636">R4</tspan>
                </text>
                <path id="BL_TO_JUNGLE" d="M625 210L625 376" stroke="#B45F06" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
              </g>

            </g>
            <g id="SYNC">
              <path id="SCART_PIN20_TO_SYNC" d="M504 742H650.829V846H916V798.5" stroke="#FFFF00" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
              <path id="CHASSIS_SYNC" fillRule="evenodd" clipRule="evenodd" d="M473.394 741.777C473.394 733.193 480.353 726.234 488.937 726.234C493.059 726.234 497.013 727.872 499.928 730.787C502.843 733.702 504.48 737.655 504.48 741.777C504.48 750.362 497.521 757.321 488.937 757.321C480.353 757.321 473.394 750.362 473.394 741.777Z" fill="#FFFF00" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
              <text id="SYNC_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-large">
                <tspan x="468" y="782.74">Sync</tspan>
              </text>
            </g>
            <g id="AUDIO">
              <g id="AUDIO_COMMON">
                <path id="SCART_PIN6_TO_R7" d="M1184.54 798.947V971.64H962.488" stroke="#B7B7B7" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="SCART_PIN6_TO_R7_2" d="M1184.54 798.947V971.64H962.488" stroke="#B7B7B7" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="SCART_PIN2_TO_R8" d="M1048.87 798.947V925.192H962.066V926.569" stroke="#EFEFEF" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="R8_TO_AUDIO_LEFT" d="M503.787 926.537H915.882" stroke="#EFEFEF" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="CHASSIS_AUDIO_LEFT" fillRule="evenodd" clipRule="evenodd" d="M472.795 926.537C472.795 918.092 479.733 911.246 488.291 911.246C492.401 911.246 496.343 912.857 499.249 915.725C502.155 918.592 503.787 922.482 503.787 926.537C503.787 934.982 496.85 941.829 488.291 941.829C479.733 941.829 472.795 934.982 472.795 926.537Z" fill="#EFEFEF" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
              </g>
              {showChassisStereo ? (
              <g id="AUD_STEREO">
                <path id="R7_TO_AUDIO_RIGHT" d="M502.791 971.646H914.633" stroke="#B7B7B7" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="CHASSIS_AUDIO_RIGHT" fillRule="evenodd" clipRule="evenodd" d="M471.798 971.646C471.798 963.2 478.736 956.354 487.294 956.354C491.404 956.354 495.346 957.965 498.252 960.833C501.158 963.701 502.791 967.59 502.791 971.646C502.791 980.091 495.853 986.937 487.294 986.937C478.736 986.937 471.798 980.091 471.798 971.646Z" fill="#FF0000" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                <text id="AUDIO_LEFT_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-large">
                  <tspan x="381" y="934.74">Audio Left</tspan>
                </text>
                <text id="AUDIO_RIGHT_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-large">
                  <tspan x="369" y="977.74">Audio Right</tspan>
                </text>
              </g>
              ):(
              <g id="MONO">
                <text id="MONO_AUDIO_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-large">
                  <tspan x="440" y="895.74">Mono Audio</tspan>
                </text>
                <path id="R7_TO_MONO_AUDIO" d="M640 930V972H916" stroke="#B7B7B7" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
              </g>
              )}
            </g>
            <g id="LEGEND">
              <text id="TRANSISTOR_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-large">
                <tspan x="158" y="873.545">Transistor</tspan>
              </text>
              <text id="INDUCTOR_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-large">
                <tspan x="158" y="927.545">Inductor</tspan>
              </text>
              <text id="RESISTOR_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-large">
                <tspan x="158" y="819.545">Resistor</tspan>
              </text>
              <text id="DIODE_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-large">
                <tspan x="158" y="765.545">Diode</tspan>
              </text>
              <text id="GROUND_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-large">
                <tspan x="158" y="711.545">Ground</tspan>
              </text>
              <text id="CAPACITOR_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-large">
                <tspan x="158" y="657.545">Capacitor</tspan>
              </text>
              <path id="TRANSISTOR_SYMBOL" d="M101.087 861C102.1 849.234 111.971 840 124 840C126.813 840 129.508 840.506 132 841.431L132 833L136 833L136 843.376C142.598 847.419 147 854.695 147 863C147 871.305 142.598 878.581 136 882.624L136 893L132 893L132 884.569C129.508 885.494 126.813 886 124 886C111.971 886 102.1 876.766 101.087 865L94 865L94 861L101.087 861ZM121.985 869.634L122.842 866.05L128.222 871.145L134.471 872.656L136 873.026L136 877.73C140.272 874.246 143 868.942 143 863C143 857.058 140.272 851.754 136 848.27L136 852.974L134.471 853.344L117 857.573L117 868.427L121.985 869.634ZM105.104 865C106.103 874.553 114.182 882 124 882C126.858 882 129.568 881.368 132 880.237L132 876.174L127.523 875.091L120.159 877.281L121.056 873.524L117 872.542L117 875L113 875L113 865L105.104 865ZM113 861L113 851L117 851L117 853.458L132 849.826L132 845.763C129.568 844.632 126.858 844 124 844C114.182 844 106.103 851.447 105.104 861L113 861Z" fill="black" />
              <path id="RESISTOR_SYMBOL" d="M146 806L141 816L133 800L125 816L117 800L109 816L101 800L96 810" stroke="black" strokeWidth="4" />
              <g id="DIODE_SYMBOL">
                <path id="Vector_56" d="M99 756.3H143.094" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="Vector_57" d="M132.362 742V769.591" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="Vector_58" fillRule="evenodd" clipRule="evenodd" d="M109.299 742.221L132.795 755.796L109.299 769.371V742.221Z" fill="black" />
              </g>
              <g id="GROUND_SYMBOL">
                <path id="Vector_59" d="M121.094 687V707.409" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="Vector_60" d="M108 707.425H134.189" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="Vector_61" d="M111.879 712.362H129.338" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="Vector_62" d="M116.724 717.298H125.454" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
              </g>
              <g id="CAPACITOR_SYMBOL">
                <path id="Vector_63" d="M142 652.989L124.646 652.989" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="Vector_64" d="M125.003 665.989L125.076 640" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="Vector_65" d="M117.003 665.989L117.076 640" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="Vector_66" d="M117 652.989L99.6457 652.989" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
              </g>
              <path id="INDUCTOR_SYMBOL" d="M132.764 914C137.35 914 139.423 917.667 140.298 920.388C140.499 921.014 140.653 921.636 140.773 922.223H147V926.223H137.314V926L137.198 926.001V925.992C137.198 925.98 137.198 925.96 137.197 925.931C137.196 925.873 137.193 925.781 137.188 925.662C137.177 925.423 137.155 925.073 137.109 924.65C137.018 923.795 136.837 922.69 136.49 921.612C135.757 919.333 134.611 918 132.764 918C130.916 918 129.77 919.333 129.037 921.612C128.691 922.69 128.511 923.795 128.419 924.65C128.374 925.073 128.351 925.423 128.34 925.662C128.334 925.781 128.331 925.873 128.33 925.931C128.329 925.96 128.329 925.98 128.329 925.992V926.001L126.329 926L124.329 926.001V925.992C124.329 925.98 124.329 925.96 124.328 925.931C124.327 925.873 124.325 925.781 124.319 925.662C124.308 925.423 124.286 925.073 124.24 924.65C124.149 923.795 123.968 922.69 123.621 921.612C122.888 919.333 121.742 918 119.895 918C118.048 918 116.902 919.333 116.169 921.612C115.822 922.69 115.642 923.795 115.55 924.65C115.504 925.073 115.482 925.423 115.471 925.662C115.465 925.781 115.463 925.873 115.462 925.931C115.461 925.96 115.461 925.98 115.461 925.992V926.001L113.461 926L111.461 926.001V925.992C111.461 925.98 111.46 925.96 111.459 925.931C111.458 925.873 111.456 925.781 111.45 925.662C111.439 925.423 111.416 925.073 111.371 924.65C111.279 923.795 111.098 922.69 110.752 921.612C110.019 919.333 108.873 918 107.026 918C105.179 918 104.033 919.333 103.3 921.612C102.953 922.69 102.772 923.795 102.681 924.65C102.642 925.008 102.622 925.314 102.609 925.544V926.223H94V922.223H99.0156C99.1361 921.636 99.2905 921.015 99.4922 920.388C100.367 917.667 102.439 914 107.026 914C110.279 914 112.267 915.845 113.46 917.889C114.653 915.845 116.641 914 119.895 914C123.148 914 125.136 915.845 126.329 917.889C127.522 915.845 129.51 914 132.764 914Z" fill="black" />
            </g>
            {showMuxTransistor  && (
            <g id="LMUX">
              <text id="S-VIDEO_DETECT_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-large">
                <tspan x="342" y="1022.74">S-Video Detect</tspan>
              </text>
              <path id="TRANSISTOR_SYMBOL_LMUX" d="M1296.09 885C1297.1 896.766 1306.97 906 1319 906C1321.81 906 1324.51 905.494 1327 904.569L1327 913L1331 913L1331 902.624C1337.6 898.581 1342 891.305 1342 883C1342 874.695 1337.6 867.419 1331 863.376L1331 853L1327 853L1327 861.431C1324.51 860.506 1321.81 860 1319 860C1306.97 860 1297.1 869.234 1296.09 881L1289 881L1289 885L1296.09 885ZM1316.99 876.366L1317.84 879.95L1323.22 874.855L1329.47 873.344L1331 872.974L1331 868.27C1335.27 871.754 1338 877.058 1338 883C1338 888.942 1335.27 894.246 1331 897.73L1331 893.026L1329.47 892.656L1312 888.427L1312 877.573L1316.99 876.366ZM1300.1 881C1301.1 871.447 1309.18 864 1319 864C1321.86 864 1324.57 864.632 1327 865.763L1327 869.826L1322.52 870.909L1315.16 868.719L1316.06 872.476L1312 873.458L1312 871L1308 871L1308 881L1300.1 881ZM1308 885L1308 895L1312 895L1312 892.542L1327 896.174L1327 900.237C1324.57 901.368 1321.86 902 1319 902C1309.18 902 1301.1 894.553 1300.1 885L1308 885Z" fill="black" />
              <path id="GROUND_TO_EMITTER" d="M1114 824H1328C1328.55 824 1329 824.448 1329 825V853" stroke="black" strokeWidth="8" />
              <path id="CHASSIS_DETECT" fillRule="evenodd" clipRule="evenodd" d="M473 1017.29C473 1008.85 479.938 1002 488.496 1002C492.606 1002 496.547 1003.61 499.453 1006.48C502.36 1009.35 503.992 1013.24 503.992 1017.29C503.992 1025.74 497.054 1032.58 488.496 1032.58C479.938 1032.58 473 1025.74 473 1017.29Z" fill="#FF7B00" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
              <path id="CHASSIS_DETECT_TO_COLLECTOR" d="M506 1017H1328C1328.55 1017 1329 1016.55 1329 1016V913" stroke="#FF7B00" strokeWidth="8" />
              <path id="SCART_PIN16_TO_R12" d="M916.5 671H857C856.448 671 856 671.448 856 672V881.5C856 882.052 856.448 882.5 857 882.5H1220.5" stroke="#B45F06" strokeWidth="8" />
              <path id="R12_TO_BASE" d="M1269.5 882.5H1289" stroke="#B45F06" strokeWidth="8" />
              <text id="R12_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-small">
                <tspan x="1237" y="910.864">R12</tspan>
              </text>
              <text id="Q1_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-small">
                <tspan x="1348" y="886.864">Q1</tspan>
              </text>
              <text id="EMITTER_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-xsmall">
                <tspan x="1317" y="855.636">E</tspan>
              </text>
              <text id="COLLECTOR_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-xsmall">
                <tspan x="1316" y="916.636">C</tspan>
              </text>
              <text id="BASE_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-xsmall">
                <tspan x="1287" y="896.636">B</tspan>
              </text>
              <path id="R12" d="M1271 881L1266 891L1258 875L1250 891L1242 875L1234 891L1226 875L1221 885" stroke="black" strokeWidth="4" />
            </g>
            )}
            <g id="GROUND">
              <text id="MAIN_CHASSIS_GROUND_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-large">
                <tspan x="433.312" y="676.545">Main Chassis&#x2028;</tspan>
                <tspan x="458.158" y="698.545">Ground</tspan>
              </text>
              <g id="MAIN_CHASSIS_GROUND">
                <path id="Vector_67" d="M489.094 618V638.409" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="Vector_68" d="M476 638.425H502.189" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="Vector_69" d="M479.879 643.362H497.338" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                <path id="Vector_70" d="M484.724 648.298H493.454" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
              </g>
            </g>
            <g id="MUX_BOARD_RESISTORS">
              <text id="R11_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-small">
                <tspan x="925" y="560.864">R11</tspan>
              </text>
              {r11Value === 'short' ? (
                <path id="R11_SHORTED" d="M848 557L900 557" stroke="black" strokeWidth="8"/>
              ) : (
                <path id="R11" d="M861 564.366L854.789 551.944L851.578 558.366L848 556.577L853 546.577L854.789 543L856.578 546.577L862.789 558.999L869 546.577L870.789 543L872.578 546.577L878.789 558.999L885 546.577L886.789 543L888.578 546.577L894.789 558.999L898 552.577L901.578 554.366L896.578 564.366L894.789 567.943L893 564.366L886.789 551.944L880.578 564.366L878.789 567.943L877 564.366L870.789 551.944L864.578 564.366L862.789 567.943L861 564.366Z" fill="black" />
              )}

              <text id="D9_TEXT" transform="translate(930 643) rotate(-90)" fill="black" xmlSpace="preserve" className="svg-text svg-text-small">
                <tspan x="0" y="11.8636">D9</tspan>
              </text>

              {showMuxBlankingDiode ? (
                <g id="D9">
                  <path id="Vector_71" d="M916 654L916 611" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                  <path id="Vector_72" d="M902 620.732L929.591 620.732" stroke="black" strokeWidth="4" strokeMiterlimit="10" strokeLinejoin="round" />
                  <path id="Vector_73" fillRule="evenodd" clipRule="evenodd" d="M902.221 643.795L915.796 620.299L929.371 643.795L902.221 643.795Z" fill="black" />
                </g>
              ) : (
                <g id="D9">
                  <line id="D9_SHORTED" x1="916" y1="611" x2="916" y2="654" stroke="black" strokeWidth="8" />
                </g>
              )}
              <text id="R8_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-small">
                <tspan x="956" y="909.864">R8</tspan>
              </text>

              {r7Value === 'short' ? (
                <>
                  <path id="R8_SHORTED" d="M914 926L966 926" stroke="black" strokeWidth="8"/>
                  <path id="R7_SHORTED" d="M914 972L966 972" stroke="black" strokeWidth="8"/>
                </>
              ) : (
                <>
                  <path id="R8" d="M965 924L960 934L952 918L944 934L936 918L928 934L920 918L915 928" stroke="black" strokeWidth="4" />
                  <path id="R7" d="M965 970L960 980L952 964L944 980L936 964L928 980L920 964L915 974" stroke="black" strokeWidth="4" />
                </>
              )}

              <text id="R7_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-small">
                <tspan x="956" y="957.864">R7</tspan>
              </text>
              <text id="R3_TEXT" transform="translate(1291 643) rotate(-90)" fill="black" xmlSpace="preserve" className="svg-text svg-text-small">
                <tspan x="0" y="11.8636">R3</tspan>
              </text>
              <path id="R3" d="M1287.37 649.578L1274.94 655.789L1281.37 659L1279.58 662.578L1269.58 657.578L1266 655.789L1269.58 654L1282 647.789L1269.58 641.578L1266 639.789L1269.58 638L1282 631.789L1269.58 625.578L1266 623.789L1269.58 622L1282 615.789L1275.58 612.578L1277.37 609L1287.37 614L1290.94 615.789L1287.37 617.578L1274.94 623.789L1287.37 630L1290.94 631.789L1287.37 633.578L1274.94 639.789L1287.37 646L1290.94 647.789L1287.37 649.578Z" fill="black" />
              <text id="R2_TEXT" transform="translate(1159 643) rotate(-90)" fill="black" xmlSpace="preserve" className="svg-text svg-text-small">
                <tspan x="0" y="11.8636">R2</tspan>
              </text>
              <path id="R2" d="M1154.37 649.578L1141.94 655.789L1148.37 659L1146.58 662.578L1136.58 657.578L1133 655.789L1136.58 654L1149 647.789L1136.58 641.578L1133 639.789L1136.58 638L1149 631.789L1136.58 625.578L1133 623.789L1136.58 622L1149 615.789L1142.58 612.578L1144.37 609L1154.37 614L1157.94 615.789L1154.37 617.578L1141.94 623.789L1154.37 630L1157.94 631.789L1154.37 633.578L1141.94 639.789L1154.37 646L1157.94 647.789L1154.37 649.578Z" fill="black" />
              <text id="R1_TEXT" transform="translate(1027 643) rotate(-90)" fill="black" xmlSpace="preserve" className="svg-text svg-text-small">
                <tspan x="0" y="11.8636">R1</tspan>
              </text>
              <path id="R1" d="M1023.37 649.578L1010.94 655.789L1017.37 659L1015.58 662.578L1005.58 657.578L1002 655.789L1005.58 654L1018 647.789L1005.58 641.578L1002 639.789L1005.58 638L1018 631.789L1005.58 625.578L1002 623.789L1005.58 622L1018 615.789L1011.58 612.578L1013.37 609L1023.37 614L1026.94 615.789L1023.37 617.578L1010.94 623.789L1023.37 630L1026.94 631.789L1023.37 633.578L1010.94 639.789L1023.37 646L1026.94 647.789L1023.37 649.578Z" fill="black" />
            </g>
            <g id="GROUND_LINES">
              <path id="CHASSIS_GROUND" fillRule="evenodd" clipRule="evenodd" d="M473.394 602.65C473.394 594.066 480.353 587.107 488.937 587.107C493.059 587.107 497.013 588.745 499.928 591.66C502.843 594.574 504.48 598.528 504.48 602.65C504.48 611.235 497.521 618.194 488.937 618.194C480.353 618.194 473.394 611.235 473.394 602.65Z" fill="black" />
              <path id="SCART_PIN21_GROUND" d="M982.024 823.946V798.934" stroke="black" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
              <path id="SCART_PIN4_GROUND" d="M1114.37 798.947V823.947H736.329V602.663H504.479" stroke="black" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
              <line id="SCART_PIN13_GROUND" x1="1049" y1="679" x2="1049" y2="606" stroke="black" strokeWidth="8" />
              <line id="SCART_PIN9_GROUND" x1="1182" y1="679" x2="1182" y2="606" stroke="black" strokeWidth="8" />
              <path id="SCART_PIN5_GROUND" d="M504.48 602.65H1314.87V678.052" stroke="black" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
              <path id="R_R1" d="M1015.52 600.189V612.221" stroke="black" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
              <path id="G_R2" d="M1146.56 601.18V613.211" stroke="black" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
              <path id="B_R3" d="M1279.55 601.18V613.211" stroke="black" strokeWidth="8" strokeMiterlimit="10" strokeLinejoin="round" />
            </g>
            <g id="FRAME_CONTENT">
              <text id="CRT_CHASSIS_TEXT" transform="translate(814.682 413.5) rotate(-90)" fill="black" xmlSpace="preserve" className="svg-text svg-text-large">
                <tspan x="2.66548" y="17.5455">CRT Chassis</tspan>
              </text>
              <text id="MUX_BOARD_TEXT" transform="translate(852.318 407.227) rotate(-90)" fill="black" xmlSpace="preserve" className="svg-text svg-text-large">
                <tspan x="2.63716" y="17.5455">MUX Board</tspan>
              </text>
              <path id="BOUNDARY" d="M842.909 11L842.909 1069" stroke="black" strokeMiterlimit="10" strokeLinejoin="round" strokeDasharray="4 3" />
            </g>
            {(!showDirectInjection || showChassisBlankingLine) && (
            <g id="OSD CHIP">
              <path id="OSD_BOX" d="M329.256 88.1378V187.138H97.5V88.1378H329.256Z" fill="#D5A6BD" stroke="black" strokeWidth="3" />
              <path id="BL_OSD_PIN_BOX" fillRule="evenodd" clipRule="evenodd" d="M114.341 186.659H143.947V210.155H114.341V186.659Z" stroke="black" strokeMiterlimit="10" strokeLinejoin="round" />
              <path id="R_OSD_PIN_BOX" fillRule="evenodd" clipRule="evenodd" d="M170.341 186.659H199.947V210.155H170.341V186.659Z" stroke="black" strokeMiterlimit="10" strokeLinejoin="round" />
              <path id="G_OSD_PIN_BOX" fillRule="evenodd" clipRule="evenodd" d="M226.341 186.659H255.947V210.155H226.341V186.659Z" stroke="black" strokeMiterlimit="10" strokeLinejoin="round" />
              <path id="B_OSD_PIN_BOX" fillRule="evenodd" clipRule="evenodd" d="M282.341 186.659H311.948V210.155H282.341V186.659Z" stroke="black" strokeMiterlimit="10" strokeLinejoin="round" />
              <text id="BL_OSD_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-medium svg-text-bold">
                <tspan x="121" y="175.318">BL</tspan>
              </text>
              <text id="R_OSD_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-medium svg-text-bold">
                <tspan x="179" y="175.318">R</tspan>
              </text>
              <text id="G_OSD_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-medium svg-text-bold">
                <tspan x="234" y="175.318">G</tspan>
              </text>
              <text id="B_OSD_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-medium svg-text-bold">
                <tspan x="292" y="175.318">B</tspan>
              </text>
              <text id="OSD_CHIP_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-large svg-text-bold">
                <tspan x="170.1" y="114.545">OSD CHIP</tspan>
              </text>
              <text id="OSD_MODEL_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-medium svg-text-center">
                <tspan x="213.378" y="140.545">{osdModelNumber}</tspan>
              </text>
              <text id="BL_OSD" fill="black" xmlSpace="preserve" className="svg-text svg-text-small svg-text-bold">
                <tspan x="122.434" y="202.864">{osdPins[3]}</tspan>
              </text>
              <text id="B_OSD" fill="black" xmlSpace="preserve" className="svg-text svg-text-small svg-text-bold">
                <tspan x="290.434" y="202.864">{osdPins[2]}</tspan>
              </text>
              <text id="G_OSD" fill="black" xmlSpace="preserve" className="svg-text svg-text-small svg-text-bold">
                <tspan x="233.434" y="202.864">{osdPins[1]}</tspan>
              </text>
              <text id="R_OSD" fill="black" xmlSpace="preserve" className="svg-text svg-text-small svg-text-bold">
                <tspan x="178.434" y="202.864">{osdPins[0]}</tspan>
              </text>
            </g>
            )}
            <g id="JUNGLE CHIP">
              <path id="JUNGLE_BOX" d="M824.256 88.1378V187.138H592.5V88.1378H824.256Z" fill="#D5A6BD" stroke="black" strokeWidth="3" />
              <path id="BL_JUNGLE_PIN_BOX" fillRule="evenodd" clipRule="evenodd" d="M610.341 186.659H639.948V210.155H610.341V186.659Z" stroke="black" strokeMiterlimit="10" strokeLinejoin="round" />
              <path id="B_JUNGLE_PIN_BOX" fillRule="evenodd" clipRule="evenodd" d="M778.341 186.659H807.948V210.155H778.341V186.659Z" stroke="black" strokeMiterlimit="10" strokeLinejoin="round" />
              <path id="G_JUNGLE_PIN_BOX" fillRule="evenodd" clipRule="evenodd" d="M722.341 186.659H751.948V210.155H722.341V186.659Z" stroke="black" strokeMiterlimit="10" strokeLinejoin="round" />
              <path id="R_JUNGLE_PIN_BOX" fillRule="evenodd" clipRule="evenodd" d="M666.341 186.659H695.948V210.155H666.341V186.659Z" stroke="black" strokeMiterlimit="10" strokeLinejoin="round" />
              <text id="BL_JUNGLE_JUNGLE_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-medium svg-text-bold">
                <tspan x="617" y="175.318">BL</tspan>
              </text>
              <text id="B_JUNGLE_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-medium svg-text-bold">
                <tspan x="788" y="175.318">B</tspan>
              </text>
              <text id="G_JUNGLE_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-medium svg-text-bold">
                <tspan x="730" y="175.318">G</tspan>
              </text>
              <text id="R_JUNGLE_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-medium svg-text-bold">
                <tspan x="675" y="175.318">R</tspan>
              </text>
              <text id="JUNGLE_CHIP_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-large svg-text-bold">
                <tspan x="648.305" y="114.545">JUNGLE CHIP</tspan>
              </text>
              <text id="JUNGLE_MODEL_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-medium svg-text-center">
                <tspan x="708.378" y="140.545">{jungleModelNumber}</tspan>
              </text>
              <text id="BL_JUNGLE" fill="black" xmlSpace="preserve" className="svg-text svg-text-small svg-text-bold">
                <tspan x="617.434" y="202.864">{junglePins[3]}</tspan>
              </text>
              <text id="B_JUNGLE" fill="black" xmlSpace="preserve" className="svg-text svg-text-small svg-text-bold">
                <tspan x="785.434" y="202.864">{junglePins[2]}</tspan>
              </text>
              <text id="G_JUNGLE" fill="black" xmlSpace="preserve" className="svg-text svg-text-small svg-text-bold">
                <tspan x="730.434" y="202.864">{junglePins[1]}</tspan>
              </text>
              <text id="R_JUNGLE" fill="black" xmlSpace="preserve" className="svg-text svg-text-small svg-text-bold">
                <tspan x="674.434" y="202.864">{junglePins[0]}</tspan>
              </text>
              {chromaVpp && (
                <text id="PEAK_TO_PEAK" transform="matrix(0 -1 1 0 564 170)" fill="black" xmlSpace="preserve" className="svg-text svg-text-medium"><tspan x="0.121094" y="18.4336">{chromaVpp}vp-p</tspan></text>
              )}
            </g>
            <g id="SCART CONNECTOR">
              <path id="Vector_74" d="M1357.68 702.554V774.444H867.648V702.554H1357.68Z" fill="#CFE2F3" stroke="black" strokeWidth="2" />
              <path id="Vector_75" fillRule="evenodd" clipRule="evenodd" d="M901.066 775H930.672V798.496H901.066V775Z" stroke="black" strokeMiterlimit="10" strokeLinejoin="round" />
              <path id="Vector_76" fillRule="evenodd" clipRule="evenodd" d="M1034.07 775H1063.67V798.496H1034.07V775Z" fill="white" />
              <path id="Vector_77" fillRule="evenodd" clipRule="evenodd" d="M1034 775H1063.61V798.496H1034V775Z" stroke="black" strokeMiterlimit="10" strokeLinejoin="round" />
              <path id="Vector_78" fillRule="evenodd" clipRule="evenodd" d="M967 775H996.606V798.496H967V775Z" stroke="black" strokeMiterlimit="10" strokeLinejoin="round" />
              <path id="Vector_79" fillRule="evenodd" clipRule="evenodd" d="M1100 775H1129.61V798.496H1100V775Z" stroke="black" strokeMiterlimit="10" strokeLinejoin="round" />
              <path id="Vector_80" fillRule="evenodd" clipRule="evenodd" d="M1169.73 775H1199.34V798.496H1169.73V775Z" stroke="black" strokeMiterlimit="10" strokeLinejoin="round" />
              <path id="Vector_81" fillRule="evenodd" clipRule="evenodd" d="M1167 679H1196.61V702.496H1167V679Z" stroke="black" strokeMiterlimit="10" strokeLinejoin="round" />
              <path id="Vector_82" fillRule="evenodd" clipRule="evenodd" d="M1234 679H1263.61V702.496H1234V679Z" stroke="black" strokeMiterlimit="10" strokeLinejoin="round" />
              <path id="Vector_83" fillRule="evenodd" clipRule="evenodd" d="M1300 679H1329.61V702.496H1300V679Z" stroke="black" strokeMiterlimit="10" strokeLinejoin="round" />
              <path id="Vector_84" fillRule="evenodd" clipRule="evenodd" d="M901 679H930.606V702.496H901V679Z" stroke="black" strokeMiterlimit="10" strokeLinejoin="round" />
              <path id="Vector_85" fillRule="evenodd" clipRule="evenodd" d="M968 679H997.606V702.496H968V679Z" stroke="black" strokeMiterlimit="10" strokeLinejoin="round" />
              <path id="Vector_86" fillRule="evenodd" clipRule="evenodd" d="M1034.07 679H1063.67V702.496H1034.07V679Z" stroke="black" strokeMiterlimit="10" strokeLinejoin="round" />
              <path id="Vector_87" fillRule="evenodd" clipRule="evenodd" d="M1101 679H1130.61V702.496H1101V679Z" stroke="black" strokeMiterlimit="10" strokeLinejoin="round" />
              <text id="5" fill="black" xmlSpace="preserve" className="svg-text svg-text-small svg-text-bold">
                <tspan x="1311" y="694.864">5</tspan>
              </text>
              <text id="7" fill="black" xmlSpace="preserve" className="svg-text svg-text-small svg-text-bold">
                <tspan x="1244" y="694.864">7</tspan>
              </text>
              <text id="9" fill="black" xmlSpace="preserve" className="svg-text svg-text-small svg-text-bold">
                <tspan x="1178" y="694.864">9</tspan>
              </text>
              <text id="11" fill="black" xmlSpace="preserve" className="svg-text svg-text-small svg-text-bold">
                <tspan x="1109" y="694.864">11</tspan>
              </text>
              <text id="13" fill="black" xmlSpace="preserve" className="svg-text svg-text-small svg-text-bold">
                <tspan x="1043" y="694.864">13</tspan>
              </text>
              <text id="15" fill="black" xmlSpace="preserve" className="svg-text svg-text-small svg-text-bold">
                <tspan x="975" y="694.864">15</tspan>
              </text>
              <text id="16" fill="black" xmlSpace="preserve" className="svg-text svg-text-small svg-text-bold">
                <tspan x="909" y="694.864">16</tspan>
              </text>
              <text id="20" fill="black" xmlSpace="preserve" className="svg-text svg-text-small svg-text-bold">
                <tspan x="908" y="790.864">20</tspan>
              </text>
              <text id="21" fill="black" xmlSpace="preserve" className="svg-text svg-text-small svg-text-bold">
                <tspan x="975" y="790.864">21</tspan>
              </text>
              <text id="2" fill="black" xmlSpace="preserve" className="svg-text svg-text-small svg-text-bold">
                <tspan x="1045" y="790.864">2</tspan>
              </text>
              <text id="4" fill="black" xmlSpace="preserve" className="svg-text svg-text-small svg-text-bold">
                <tspan x="1110" y="790.864">4</tspan>
              </text>
              <text id="6" fill="black" xmlSpace="preserve" className="svg-text svg-text-small svg-text-bold">
                <tspan x="1181" y="790.864">6</tspan>
              </text>
              <text id="SCART CONNECTOR_2" fill="black" xmlSpace="preserve" className="svg-text svg-text-xlarge svg-text-bold">
                <tspan x="1013" y="749.273">SCART CONNECTOR</tspan>
              </text>
            </g>

            <g id="MODEL">
              <text id="CHASSIS" fill="black" xmlSpace="preserve" className="svg-text svg-text-xlarge svg-text-bold svg-text-center">
                <tspan x="1130" y="88.2727">{chassisString}</tspan>
              </text>
              <text id="RGB MUX Diagram&#226;&#128;&#168;Sunthar" fill="black" xmlSpace="preserve" className="svg-text svg-text-xlarge svg-text-bold">
                <tspan x="1039.01" y="165.273">RGB MUX Diagram&#x2028;</tspan>
                <tspan x="1075.51" y="189.273">Sunthar</tspan>
              </text>
              {/* Only show model number if not showing warning overlay */}
              {!(selectedCrtModel?.slug && !token && !crtDataLoaded && crtModelNumber && !valuesWarningDismissed) && (
                <text id="CRT_MODEL_NUMBER" fill="black" xmlSpace="preserve" className="svg-text svg-text-heading svg-text-center">
                  <tspan x="1130" y="131.136">{crtModelNumber}</tspan>
                </text>
              )}
            </g>

            <g id="MUXBOARD_SUMMARY">
              <text id="R1_R2_R3_SUMMARY_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-xlarge">
                <tspan x="1036.41" y="227.434">R1, R2, R3:</tspan>
              </text>
              <text id="R4_R5_R6_SUMMARY_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-xlarge">
                <tspan x="1036.41" y="254.434">R4, R5, R6:</tspan>
              </text>
              <text id="R7_R8_SUMMARY_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-xlarge">
                <tspan x="1073.13" y="281.434">R7, R8:</tspan>
              </text>
              <text id="D9_SUMMARY_TEXT" fill="black" className="svg-text svg-text-xlarge">
                <tspan x="1109.85" y="308.434">D9:</tspan>
              </text>
              <text id="R10_SUMMARY_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-xlarge">
                <tspan x="1098.71" y="335.434">R10:</tspan>
              </text>
              <text id="R11_SUMMARY_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-xlarge">
                <tspan x="1100.2" y="362.434">R11:</tspan>
              </text> 
              {showMuxTransistor && ( 
                <>
                  <text id="R12_SUMMARY_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-xlarge">
                    <tspan x="1098.71" y="389.434">R12:</tspan>
                  </text>
                  <text id="Q1_SUMMARY_TEXT" fill="black" xmlSpace="preserve" className="svg-text svg-text-xlarge">
                    <tspan x="1108.74" y="416.434">Q1:</tspan>
                  </text>
                </> 
              )} 
              <text id="R1_R2_R3_VALUE" fill="black" xmlSpace="preserve" className="svg-text svg-text-xlarge">
                <tspan x="1148" y="227.434">{r1Value}</tspan>
              </text>
              <text id="R4_R5_R6_VALUE" fill="black" xmlSpace="preserve" className="svg-text svg-text-xlarge">
                <tspan x="1148" y="254.434">{r4Value}</tspan>
              </text>
              <text id="R7_R8_VALUE" fill="black" xmlSpace="preserve" className="svg-text svg-text-xlarge">
                <tspan x="1148" y="281.434">{r7Value}</tspan>
              </text>
              <text id="D9_VALUE" fill="black" xmlSpace="preserve" className="svg-text svg-text-xlarge">
                <tspan x="1148" y="308.434">{r9Value}</tspan>
              </text>
              <text id="R10_VALUE" fill="black" xmlSpace="preserve" className="svg-text svg-text-xlarge">
                <tspan x="1148" y="335.434">{r10Value}</tspan>
              </text>
              <text id="R11_VALUE" fill="black" xmlSpace="preserve" className="svg-text svg-text-xlarge">
                <tspan x="1148" y="362.434">{r11Value}</tspan>
              </text> 
              {showMuxTransistor && ( 
                <>
                  <text id="R12_VALUE" fill="black" xmlSpace="preserve" className="svg-text svg-text-xlarge">
                    <tspan x="1148" y="389.434">{r12Value}</tspan>
                  </text>
                  <text id="Q1_VALUE" fill="black" xmlSpace="preserve" className="svg-text svg-text-xlarge">
                    <tspan x="1148" y="416.434">PN2222A</tspan>
                  </text>
                </> 
              )} 
            </g>
          </g>
          <g id="DISCLAIMER">
            <text id="Original OSD Mux Design: Syntax &#38; MarkOZLAD&#226;&#128;&#168;Enhanced Design: Sunthar" fill="black" xmlSpace="preserve" className="svg-text svg-text-normal">
              <tspan x="37.8369" y="1049.59">Original OSD Mux Design: Syntax &#38; MarkOZLAD&#x2028;</tspan>
              <tspan x="108.63" y="1066.59">Enhanced Design: Sunthar</tspan>
            </text>
            <text id="Copyright &#194;&#169; 2025 Sunthar&#39;s Super Sector" fill="black" xmlSpace="preserve" className="svg-text svg-text-normal">
              <tspan x="1058.78" y="1050.59">Copyright &#xa9; 2025 Sunthar&#39;s Super Sector</tspan>
            </text>
          </g>


          <g id="SUNTHAR_LOGO">
            <g id="ICON">
              <g id="A_SIDES">
                <g id="RED">
                  <mask id="mask0_1_731" style={{maskType: "luminance"}} maskUnits="userSpaceOnUse" x="1311" y="30" width="60" height="58">
                    <g id="Group">
                      <path id="Vector_88" d="M1319.88 72.0087L1313.57 86.9956C1313.36 87.4755 1312.98 87.7141 1312.43 87.7141C1312.09 87.7141 1311.79 87.5956 1311.55 87.3551C1311.31 87.1159 1311.19 86.8257 1311.19 86.4823C1311.19 86.2773 1311.22 86.1071 1311.29 85.9691L1332.31 36.0814C1333.06 34.234 1334.24 32.8321 1335.82 31.8731L1336.03 31.6678C1337.55 30.7791 1339.2 30.3333 1341 30.3333C1342.93 30.3333 1344.69 30.8466 1346.28 31.8731L1346.38 31.9755H1346.49C1347.93 33.002 1349.04 34.3718 1349.8 36.0814L1370.81 85.9691V86.3797C1370.81 87.2699 1370.4 87.7141 1369.57 87.7141C1368.95 87.7141 1368.6 87.4755 1368.53 86.9956L1362.22 72.0087H1319.88Z" fill="white" />
                    </g>
                  </mask>
                  <g mask="url(#mask0_1_731)">
                    <path id="Rectangle" d="M1370.81 30.3333H1340.9V87.7141H1370.81V30.3333Z" fill="#FF005D" />
                  </g>
                </g>
                <g id="GREEN">
                  <mask id="mask1_1_731" style={{maskType: "luminance"}} maskUnits="userSpaceOnUse" x="1311" y="30" width="60" height="58">
                    <g id="Group_2">
                      <path id="Vector_89" d="M1319.88 72.0087L1313.57 86.9956C1313.36 87.4755 1312.98 87.7141 1312.43 87.7141C1312.09 87.7141 1311.79 87.5956 1311.55 87.3551C1311.31 87.1159 1311.19 86.8257 1311.19 86.4823C1311.19 86.2773 1311.22 86.1071 1311.29 85.9691L1332.31 36.0814C1333.06 34.234 1334.24 32.8321 1335.82 31.8731L1336.03 31.6678C1337.55 30.7791 1339.2 30.3333 1341 30.3333C1342.93 30.3333 1344.69 30.8466 1346.28 31.8731L1346.38 31.9755H1346.49C1347.93 33.002 1349.04 34.3718 1349.8 36.0814L1370.81 85.9691V86.3797C1370.81 87.2699 1370.4 87.7141 1369.57 87.7141C1368.95 87.7141 1368.6 87.4755 1368.53 86.9956L1362.22 72.0087H1319.88Z" fill="white" />
                    </g>
                  </mask>
                  <g mask="url(#mask1_1_731)">
                    <path id="Rectangle_2" d="M1310.78 30.3333H1341.1V87.7141H1310.78V30.3333Z" fill="#BDEA88" />
                  </g>
                </g>
              </g>
              <g id="CIRCUIT">
                <g id="CIRCUIT_LINES">
                  <g id="Vector_90">
                    <path fillRule="evenodd" clipRule="evenodd" d="M1341.06 72.0463V47.7009V72.0463Z" fill="#E64D85" />
                    <path d="M1341.06 72.0463V47.7009" stroke="black" strokeWidth="2.5" />
                  </g>
                  <path id="Stroke-3" fillRule="evenodd" clipRule="evenodd" d="M1343.65 45.3916C1343.65 46.853 1342.47 48.0377 1341 48.0377C1339.53 48.0377 1338.35 46.853 1338.35 45.3916C1338.35 43.9302 1339.53 42.7455 1341 42.7455C1342.47 42.7455 1343.65 43.9302 1343.65 45.3916Z" stroke="black" strokeWidth="2.5" />
                  <path id="Stroke-5" fillRule="evenodd" clipRule="evenodd" d="M1332.78 57.1377C1332.78 58.5991 1331.59 59.7838 1330.12 59.7838C1328.66 59.7838 1327.47 58.5991 1327.47 57.1377C1327.47 55.6763 1328.66 54.4916 1330.12 54.4916C1331.59 54.4916 1332.78 55.6763 1332.78 57.1377Z" stroke="black" strokeWidth="2.5" />
                  <path id="Path" d="M1332.13 58.8803L1335.76 62.4867V72.0463" stroke="black" strokeWidth="2.5" />
                  <path id="Stroke-9" fillRule="evenodd" clipRule="evenodd" d="M1355.96 60.2355C1355.96 61.6969 1354.77 62.8816 1353.3 62.8816C1351.84 62.8816 1350.65 61.6969 1350.65 60.2355C1350.65 58.7741 1351.84 57.5894 1353.3 57.5894C1354.77 57.5894 1355.96 58.7741 1355.96 60.2355Z" stroke="black" strokeWidth="2.5" />
                  <path id="Stroke-11" d="M1351.16 61.8491L1346.76 65.4544V72.0463" stroke="black" strokeWidth="2.5" />
                </g>
              </g>
            </g>
          </g>
          {crtRgbModifiable === 'No' && (
          <text
            id="NOT_EASILY_RGB_MODDABLE"
            transform="translate(365 825.032) rotate(-45)"
            fill="#FF0000"
            xmlSpace="preserve"
            className="svg-text svg-text-not-rgb-modifiable"
          >
            <tspan x="0" y="61.7727">NOT EASILY RGB MODDABLE</tspan>
          </text>
          )}
          
          {/* Show warning overlay when CRT model is loaded from backend but user is not authenticated */}
          {selectedCrtModel?.slug && !token && !crtDataLoaded && crtModelNumber && !valuesWarningDismissed && (
            <g id="VALUES_NOT_LOADED_OVERLAY">
              {/* Semi-transparent overlay */}
              <rect x="0" y="0" width="1400" height="1080" fill="rgba(255, 255, 255, 0.9)" />
              
              {/* Warning message */}
              <g transform="translate(700, 540)">
                {/* Warning icon background */}
                <circle cx="0" cy="-30" r="50" fill="#ffc107" stroke="#ffb607" strokeWidth="3"/>
                
                {/* Warning icon */}
                <text x="0" y="-15" textAnchor="middle" fill="#000" fontSize="50" fontWeight="bold">⚠</text>
                
                {/* Main warning text */}
                <text x="0" y="60" textAnchor="middle" fill="#000" fontWeight="bold" className="svg-text svg-text-heading">
                  <tspan x="0" dy="0">Circuit values not loaded!</tspan>
                </text>
                
                {/* Subtitle */}
                <text x="0" y="120" textAnchor="middle" fill="#666" fontWeight="bold" className="svg-text svg-text-title">
                  <tspan x="0" dy="0">Login required to load accurate values for {crtModelNumber}</tspan>
                </text>
              </g>
              
              {/* Close button positioned at top right corner */}
              <g 
                transform="translate(1350, 50)" 
                style={{cursor: 'pointer'}}
                onClick={() => setValuesWarningDismissed(true)}
              >
                {/* Close button background */}
                <circle cx="0" cy="0" r="25" fill="#ff0000" stroke="#000000" strokeWidth="3"/>
                
                {/* X icon */}
                <line x1="-10" y1="-10" x2="10" y2="10" stroke="white" strokeWidth="3" strokeLinecap="round"/>
                <line x1="10" y1="-10" x2="-10" y2="10" stroke="white" strokeWidth="3" strokeLinecap="round"/>
              </g>
            </g>
          )}
        </g>
        <defs>
          <clipPath id="clip0_1_731">
            <rect width="1400" height="1080" fill="white" />
          </clipPath>
        </defs>
      </svg>
    );
  };

  // Handle model number changes
  const handleOsdModelNumberChange = (e) => {
    setOsdModelNumber(e.target.value);
  };

  const handleJungleModelNumberChange = (e) => {
    setJungleModelNumber(e.target.value);
  };

  const handleCrtModelNumberChange = (e) => {
    const newValue = e.target.value;
    setCrtModelNumber(newValue);
    // Also update the selectedCrtModel state to keep them in sync
    if (newValue) {
      setSelectedCrtModel({ 
        value: newValue, 
        label: newValue 
      });
    } else {
      setSelectedCrtModel(null);
    }
  };

  // Function to download diagram as PNG
  const handleDownloadDiagram = () => {
    try {
      const svgElement = document.querySelector('.svg-diagram');
      if (!svgElement) {
        console.error('SVG element not found');
        return;
      }
      
      // Create a simpler SVG clone with optimized dimensions
      const svgClone = svgElement.cloneNode(true);
      
      // Use a wider aspect ratio to match the reference image
      const width = 1400; 
      const height = 1080; 
      
      // Add necessary attributes for proper rendering
      svgClone.setAttribute('width', width);
      svgClone.setAttribute('height', height);
      svgClone.setAttribute('viewBox', '0 0 1400 1080');
      svgClone.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      svgClone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
      
      // Prepare CSS styles to be inlined in the SVG
      const svgStyles = `
        .svg-text { font-family: Arial, sans-serif; }
        .svg-text-heading { font-size: 32px; font-weight: bold; }
        .svg-text-title { font-size: 24px; font-weight: bold; }
        .svg-text-xlarge { font-size: 20px; }
        .svg-text-large { font-size: 18px; }
        .svg-text-medium { font-size: 16px; }
        .svg-text-normal { font-size: 14px; }
        .svg-text-small { font-size: 12px; }
        .svg-text-xsmall { font-size: 10px; }
        .svg-text-bold { font-weight: bold; }
        .svg-text-center { text-anchor: middle; }
      `;
      
      // Inject styles directly into the SVG
      const styleElement = document.createElementNS('http://www.w3.org/2000/svg', 'style');
      styleElement.textContent = svgStyles;
      svgClone.insertBefore(styleElement, svgClone.firstChild);
      
      // Fix any background elements to ensure they cover the whole image
      const rectElement = svgClone.querySelector('rect[width="1400"][height="1036"]');
      if (rectElement) {
        rectElement.setAttribute('width', width);
        rectElement.setAttribute('height', height);
      }
      
      // Convert SVG to string and ensure all styles are properly embedded
      const serializer = new XMLSerializer();
      let svgString = serializer.serializeToString(svgClone);

      // Try simpler approach first for better browser compatibility
      try {
        // Create canvas directly
        const canvas = document.createElement('canvas');
        const scale = 2;
        canvas.width = width * scale;
        canvas.height = height * scale;
        
        // Create an image and set source to a data URL
        const img = new Image();
        img.width = width;
        img.height = height;
        
        // Convert SVG to data URL
        const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        const reader = new FileReader();
        
        reader.onload = function(e) {
          img.onload = function() {
            // Draw on canvas once image is loaded
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.scale(scale, scale);
            ctx.drawImage(img, 0, 0, width, height);
            
            // Get PNG data URL
            try {
              const pngUrl = canvas.toDataURL('image/png', 1.0);
              
              // Trigger download
              const a = document.createElement('a');
              a.href = pngUrl;
              a.download = `RGB_MUX_Diagram_${crtModelNumber.replace(/\s+/g, '_')}.png`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
            } catch (err) {
              console.error('Canvas export error:', err);
              alert('Error creating PNG. Your browser might have security restrictions for canvas operations.');
            }
          };
          
          // Set the image source to the SVG data URL
          img.src = e.target.result;
        };
        
        // Read the SVG blob as data URL
        reader.readAsDataURL(svgBlob);
        
      } catch (fallbackError) {
        console.error('Direct approach failed, trying fallback method:', fallbackError);
        alert('Encountered an error. The download may not work in this browser. Please try Chrome or Firefox.');
      }
      
    } catch (error) {
      console.error('PNG Export Error:', error);
      alert('Error creating PNG: ' + error.message);
    }
  };

  // Handle formula card toggle
  const toggleFormulaCard = () => {
    setFormulaCardCollapsed(!formulaCardCollapsed);
  };

  // Handle voltage card toggle
  const toggleVoltageCard = () => {
    setVoltageCardCollapsed(!voltageCardCollapsed);
  };

  // Debounce function
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  // Fetch CRT model suggestions with debounce
  const debouncedFetch = useCallback(
    debounce(async (inputValue) => {
      if (inputValue.length < 2) {
        setSuggestions([]);
        return;
      }

      setIsLoadingModels(true);

      try {
        const headers = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
        
        const res = await fetch(`${SUNTHAR_API_URL}/crts/autocomplete?q=${encodeURIComponent(inputValue)}`, {
          headers
        });
        
        if (!res.ok) {
          toast.error(`Autocomplete failed: ${res.status}`);
        }
        
        const data = await res.json();

        const formatted = (data.suggestions || []).map((item) => ({
          value: item.title,
          label: item.title,
          slug: item.slug || null
          // Circuit data will be fetched separately from public/:slug endpoint
        }));

        setSuggestions(formatted);
      } catch (err) {
        console.error('Autocomplete error:', err);
        setSuggestions([]);
      } finally {
        setIsLoadingModels(false);
      }
    }, 300),
    [token]
  );

  // Handle CRT model change
  const handleCrtModelChange = (selected) => {
    if (selected) {
      setSelectedCrtModel(selected);
      setCrtModelNumber(selected.value);
      
      // If we have a slug, fetch the complete CRT data
      if (selected.slug) {
        fetchCrtDetails(selected.slug);
      }
    } else {
      setSelectedCrtModel(null);
      setCrtModelNumber(null);
      setCrtRgbModifiable(null);
      setCrtModBoards([]);
      setCrtRgbModNotes('');
      setCrtRgbModLink('');
      setAuthMessage('');
      setValuesWarningDismissed(false);
    }
  };
  
  // Fetch complete CRT details by slug
  const fetchCrtDetails = async (slug) => {
    try {
      // Clear any previous auth message
      setAuthMessage('');
      setCrtDataLoaded(false);
      
      // First, get basic info from public endpoint
      const publicRes = await fetch(`${SUNTHAR_API_URL}/crts/public/${slug}`);
      
      if (!publicRes.ok) {
        throw new Error(`Failed to fetch CRT details: ${publicRes.status}`);
      }
      
      const publicData = await publicRes.json();
      
      // Store the RGB modifiable status
      setCrtRgbModifiable(publicData.rgb_modifiable);
      
      // Store mod boards if available
      setCrtModBoards(publicData.mod_boards || []);
      
      // Store RGB modification link if available
      setCrtRgbModLink(publicData.rgb_mod_link || '');
      
      // Set warning message if CRT is not RGB modifiable
      if (publicData.rgb_modifiable === 'No') {
        setAuthMessage('This CRT model is confirmed to NOT support RGB modifications.');
        return; // Exit early since RGB modification is not possible
      }
      
      // Check if RGB calculator is enabled for this CRT
      if (publicData.rgb_mux_calculator_enabled === 'true' || publicData.rgb_mux_calculator_enabled === true) {
        setCalculatorEnabled(true);
      } else {
        setCalculatorEnabled(false);
        // If calculator is not enabled, show message and return early
        setAuthMessage('Pre-calculated values are not available for this CRT model.');
        return;
      }
      
      // Store basic info for use after authentication, and populate basic fields now
      const basicCrtInfo = {
        title: publicData.title,
        osd: publicData.osd,
        chroma: publicData.chroma,
        slug: slug,
        id: publicData._id
      };

      // Set basic CRT info that should be visible even when not logged in
      if (basicCrtInfo.title) {
        setCrtModelNumber(basicCrtInfo.title);
        setSelectedCrtModel({ 
          value: basicCrtInfo.title, 
          label: basicCrtInfo.title,
          slug: basicCrtInfo.slug,
          id: basicCrtInfo.id
        });
        // Reset warning dismissal when new model is loaded
        setValuesWarningDismissed(false);
      }
      
      if (basicCrtInfo.osd) {
        setOsdModelNumber(basicCrtInfo.osd);
      }
      
      if (basicCrtInfo.chroma) {
        setJungleModelNumber(basicCrtInfo.chroma);
      }

      // Now fetch circuit data from authenticated endpoint
      try {
        const circuitRes = await fetch(`${SUNTHAR_API_URL}/crts/circuit/${slug}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (circuitRes.ok) {
          const circuitData = await circuitRes.json();
          
          // Apply circuit data from authenticated endpoint
          applyCrtCircuitData(circuitData);
          
          // Store RGB modification notes if available
          setCrtRgbModNotes(circuitData.rgb_mod_notes || '');
          
          setCrtDataLoaded(true);
        } else if (circuitRes.status === 429) {
          // Handle rate limit exceeded
          setCrtRgbModNotes(''); // Clear notes on rate limit
          try {
            const errorData = await circuitRes.json();
            const resetHours = errorData.resetHours || 24;
            setAuthMessage(`Daily access limit exceeded. Please try again in ${resetHours} hours. You can access ${errorData.limits?.userDaily?.limit || 10} circuit configurations per day.`);
          } catch (parseError) {
            setAuthMessage('Daily access limit exceeded. Please try again in 24 hours.');
          }
        } else if (circuitRes.status === 401 || circuitRes.status === 403) {
          // Handle authentication/authorization errors
          console.log('Authentication required for circuit data');
          setCrtRgbModNotes(''); // Clear notes since auth failed
          setAuthMessage(
            <>
              This CRT model ({basicCrtInfo.title || 'Unknown'}) has RGB calculator data available. Please <a href="#" onClick={(e) => { e.preventDefault(); handleLogin(); }} style={{ cursor: 'pointer', textDecoration: 'underline' }}>login</a> to access the complete circuit configuration.
            </>
          );
          
          // Set redirect for after login and navigate to login page
          toast.info('Please log in to access circuit data for this CRT model.');
        } else {
          // Handle other errors
          console.log('Error fetching circuit data:', circuitRes.status);
          setCrtRgbModNotes(''); // Clear notes on error
          setAuthMessage('Circuit data is currently unavailable.');
        }
      } catch (circuitErr) {
        console.error('Error fetching circuit data:', circuitErr);
        setCrtRgbModNotes(''); // Clear notes on error
        // Show an error message about circuit data being unavailable
        setAuthMessage('Circuit data is currently unavailable.');
      }
    } catch (err) {
      console.error('Error fetching CRT details:', err);
      setAuthMessage('Failed to load CRT information.');
    }
  };
  
  // Create a blank circuit state for CRT-specific loading
  const createBlankCircuitState = () => {
    return {
      r1: '',
      r2: '',
      r3: '',
      r4: '',
      r5: '',
      r6: '',
      r7: '',
      r8: '',
      r9: '',
      r10: '',
      r11: '',
      r12: '',
      r201: '',
      r202: '',
      r203: '',
      r204: '',
      r205: '',
      r206: '',
      r207: '',
      r208: '',
      chassis_blanking_inductor_number: '',
      inputRedVoltage: 0.7,
    };
  };

  const applyCrtCircuitData = (data) => {
    // Parse and set circuit values
    // Use blank circuit state as starting point when loading CRT data
    const newCircuit = createBlankCircuitState();
    
         if (data.chassis?.[0]) {
       setChassisString(data.chassis[0]);
      }

      // Set chromaVpp if available
      if (data.analog_chroma_vpp) {
        setChromaVpp(data.analog_chroma_vpp);
      }
      
      // Parse pin numbers for OSD and Jungle chips
    if (data.chassis_inline_osd_pin_numbers) {
      try {
        const osdPinArray = data.chassis_inline_osd_pin_numbers.split(',');
        if (osdPinArray.length >= 4) {
          // OSD pin order is R, G, B, BL
          setOsdPins([
            osdPinArray[0].trim(), // R
            osdPinArray[1].trim(), // G
            osdPinArray[2].trim(), // B
            osdPinArray[3].trim()  // BL
          ]);
        }
      } catch (error) {
        console.error("Error parsing OSD pin numbers:", error);
      }
    }
    
    if (data.chassis_inline_jungle_pin_numbers) {
      try {
        const junglePinArray = data.chassis_inline_jungle_pin_numbers.split(',');
        if (junglePinArray.length >= 4) {
          // Jungle pin order is R, G, B, BL
          setJunglePins([
            junglePinArray[0].trim(), // R
            junglePinArray[1].trim(), // G
            junglePinArray[2].trim(), // B
            junglePinArray[3].trim()  // BL
          ]);
        }
      } catch (error) {
        console.error("Error parsing Jungle pin numbers:", error);
      }
    }
    
    // Parse chassis resistor identifiers
    if (data.chassis_inline_resistor_numbers) {
      try {
        const resistorIdArray = data.chassis_inline_resistor_numbers.split(',');
        if (resistorIdArray.length >= 4) {
          setChassisResistorIds([
            resistorIdArray[0].trim(), // First resistor (R201)
            resistorIdArray[1].trim(), // Second resistor (R202)
            resistorIdArray[2].trim(), // Third resistor (R203)
            resistorIdArray[3].trim()  // Fourth resistor (R208)
          ]);
        }
      } catch (error) {
        console.error("Error parsing chassis resistor identifiers:", error);
      }
    }
    
    // Parse chassis ground resistor identifiers
    if (data.chassis_ground_resistor_numbers) {
      try {
        const groundResistorIdArray = data.chassis_ground_resistor_numbers.split(',');
        setChassisGroundResistorIds([
          groundResistorIdArray[0]?.trim() || '',
          groundResistorIdArray[1]?.trim() || '',
          groundResistorIdArray[2]?.trim() || '',
          groundResistorIdArray[3]?.trim() || ''
        ]);
        
        // If there are fewer than 4 resistors and no blanking ground resistor value,
        // set r207 to null or 0 to hide it from the diagram
        if (groundResistorIdArray.length < 4 && !data.chassis_blanking_ground_resistor_value) {
          newCircuit.r207 = null;
        }
      } catch (error) {
        console.error("Error parsing chassis ground resistor identifiers:", error);
      }
    }
    
    // Handle chassis inline resistors
    if (data.chassis_inline_resistors) {
      const inlineValue = parseInt(data.chassis_inline_resistors, 10);
      if (!isNaN(inlineValue)) {
        newCircuit.r201 = inlineValue;
        newCircuit.r202 = inlineValue;
        newCircuit.r203 = inlineValue;
      }
    }
    
    // Handle chassis ground resistors
    if (data.chassis_ground_resistors) {
      const groundValue = parseInt(data.chassis_ground_resistors, 10);
      if (!isNaN(groundValue)) {
        newCircuit.r204 = groundValue;
        newCircuit.r205 = groundValue;
        newCircuit.r206 = groundValue;
      }
    }

    // Handle chassis blanking inline resistor value specifically for r208
    if (data.chassis_inline_blanking_resistor_value) {
      const blankingInlineValue = parseInt(data.chassis_inline_blanking_resistor_value, 10);
      if (!isNaN(blankingInlineValue)) {
        newCircuit.r208 = blankingInlineValue;
      }
    }
    
    // Handle chassis blanking ground resistor value specifically for r207
    if (data.chassis_blanking_ground_resistor_value) {
      const blankingGroundValue = parseInt(data.chassis_blanking_ground_resistor_value, 10);
      if (!isNaN(blankingGroundValue)) {
        newCircuit.r207 = blankingGroundValue;
      }
    }
    
    // Handle MUX termination resistors
    if (data.mux_termination_resistors) {
      const terminationValue = parseInt(data.mux_termination_resistors, 10);
      if (!isNaN(terminationValue)) {
        newCircuit.r1 = terminationValue;
        newCircuit.r2 = terminationValue;
        newCircuit.r3 = terminationValue;
      }
    }
    
    // Handle MUX RGB input resistors (when not in direct injection mode)
    if (data.mux_rgb_input_resistors) {
      const rgbInputValue = parseInt(data.mux_rgb_input_resistors, 10);
      if (!isNaN(rgbInputValue)) {
        newCircuit.r4 = rgbInputValue;
        newCircuit.r5 = rgbInputValue;
        newCircuit.r6 = rgbInputValue;
      }
    }
    
    // Handle MUX audio resistors
    if (data.mux_audio_resistors) {
      const audioValue = parseInt(data.mux_audio_resistors, 10);
      if (!isNaN(audioValue)) {
        newCircuit.r7 = audioValue;
        newCircuit.r8 = audioValue;
      }
    }
    
    // Handle MUX blanking ground resistor
    if (data.mux_blanking_ground_resistor) {
      const blankingGroundValue = parseInt(data.mux_blanking_ground_resistor, 10);
      if (!isNaN(blankingGroundValue)) {
        newCircuit.r10 = blankingGroundValue;
      } else {
        newCircuit.r10 = '';
      }
    } else {
      // If mux_blanking_ground_resistor is missing entirely, set r10 to empty
      newCircuit.r10 = '';
    }
    
    // Handle MUX blanking inline resistor
    if (data.mux_blanking_inline_resistor) {
      const blankingInlineValue = parseInt(data.mux_blanking_inline_resistor, 10);
      if (!isNaN(blankingInlineValue)) {
        newCircuit.r11 = blankingInlineValue;
      }
    }
    
    // Handle chassis blanking diode for inline diode toggle
    if (data.chassis_blanking_diode !== undefined) {
      setShowChassisBlankingDiode(data.chassis_blanking_diode === 'true');
    } else {
      // If value is not provided by backend, use a default of false
      setShowChassisBlankingDiode(false);
    }
    
    // Handle RGB input diodes
    if (data.chassis_r_g_b_diodes !== undefined) {
      setShowChassisRGBDiodes(data.chassis_r_g_b_diodes === 'true');
    } else {
      // If value is not provided by backend, use a default of false
      setShowChassisRGBDiodes(false);
    }
    
    // Handle MUX blanking diode
    if (data.mux_blanking_diode !== undefined) {
      setShowMuxBlankingDiode(data.mux_blanking_diode === 'true');
    } else {
      // If value is not provided by backend, use a default of false
      setShowMuxBlankingDiode(false);
    }

    // Handle analog chroma Vpp
    if (data.analog_chroma_vpp) {
      const vppValue = parseFloat(data.analog_chroma_vpp);
      if (!isNaN(vppValue)) {
        setVppInputVoltage(vppValue);
      }
    }
    
    // Handle chassis capacitor numbers
    if (data.chassis_capacitor_numbers) {
      setChassisCapacitorNumbers(data.chassis_capacitor_numbers);
    }
    
    // Handle chassis capacitor values
    if (data.chassis_capacitor_value) {
      setChassisCapacitorValues(data.chassis_capacitor_value);
    }
    
    // Handle MUX transistor
    if (data.mux_transistor !== undefined) {
      setShowMuxTransistor(data.mux_transistor === 'true');
    } else {
      // If value is not provided by backend, use a default of false
      setShowMuxTransistor(false);
    }
    
    // Handle chassis stereo
    if (data.chassis_stereo !== undefined) {
      setShowChassisStereo(data.chassis_stereo === 'true');
    } else {
      // If value is not provided by backend, use a default of false
      setShowChassisStereo(false);
    }
    
    // Handle RGB direct injection
    if (data.rgb_direct_injection !== undefined) {
      setShowDirectInjection(data.rgb_direct_injection === 'true');
    } else {
      // If value is not provided by backend, use a default of false
      setShowDirectInjection(false);
    }
    
    // Handle chassis blanking inductor
    if (data.chassis_blanking_inductor !== undefined) {
      setShowChassisBlankingInductor(data.chassis_blanking_inductor === 'true');
    } else {
      // If value is not provided by backend, use a default of false
      setShowChassisBlankingInductor(false);
    }
    
    // Handle chassis blanking line
    if (data.chassis_blanking_line_enabled !== undefined) {
      setShowChassisBlankingLine(data.chassis_blanking_line_enabled === 'true');
    } else {
      // If value is not provided by backend, use a default of true (blanking line enabled)
      setShowChassisBlankingLine(true);
    }
    
    // Handle change caps label
    if (data.chassis_change_caps_label !== undefined) {
      setShowChangeCapsLabel(data.chassis_change_caps_label === 'true');
    } else {
      // If value is not provided by backend, use a default of false
      setShowChangeCapsLabel(false);
    }
    
    // Handle chassis blanking diode position
    if (data.chassis_blanking_diode_position_before_ground !== undefined) {
      setBlankingDiodeBeforeR207(data.chassis_blanking_diode_position_before_ground === 'true');
    } else {
      // If value is not provided by backend, use a default of false
      setBlankingDiodeBeforeR207(false);
    }
    
    // Handle chassis blanking diode needs adding
    if (data.chassis_blanking_diode_added !== undefined) {
      // Update the state variable instead of circuit
      setShowChassisBlankingAddDiodeLabel(data.chassis_blanking_diode_added === 'true');
    } else {
      // If value is not provided by backend, use a default of true (show "add diode" label by default)
      newCircuit.chassis_blanking_diode_added = 'true';
    }

    // Handle RGB diodes needs adding
    if (data.chassis_r_g_b_diodes_added !== undefined) {
      // Update circuit state to store this information
      setShowChassisRGBAddDiodesLabel(data.chassis_r_g_b_diodes_added === 'true');
    } else {
      // If value is not provided by backend, use a default of true (show "add diodes" label by default)
      newCircuit.chassis_r_g_b_diodes_added = 'true';
    }
    
    // Handle MUX RGB input capacitors (when in direct injection mode)
    if (data.mux_rgb_input_capacitors) {
      const capacitorValue = parseFloat(data.mux_rgb_input_capacitors);
      if (!isNaN(capacitorValue)) {
        setDirectInjectionCaps(prev => ({
          ...prev,
          c4: capacitorValue,
          c5: capacitorValue,
          c6: capacitorValue
        }));
      }
    }
    
    // Handle MUX transistor base resistor
    if (data.mux_transistor_base_resistor) {
      const baseResistorValue = parseInt(data.mux_transistor_base_resistor, 10);
      if (!isNaN(baseResistorValue)) {
        newCircuit.r12 = baseResistorValue;
      }
    }
    
    // Handle chassis blanking inductor number
    if (data.chassis_blanking_inductor_number !== undefined) {
      newCircuit.chassis_blanking_inductor_number = data.chassis_blanking_inductor_number;
    }
    
    setCircuit(newCircuit);
  };

  // Add state for share link notification
  const [shareNotification, setShareNotification] = useState('');
  // Add state to store the generated shareable link
  const [shareableLink, setShareableLink] = useState('');
  // State to track if values warning overlay has been dismissed
  const [valuesWarningDismissed, setValuesWarningDismissed] = useState(false);

  // Function to generate a shareable link with current circuit values
  const generateShareableLink = () => {
    try {
      // Create a data object with all circuit values
      const shareData = {
        // Essential resistors
        cr: circuit.r201, // Chassis inline resistors
        cg: circuit.r204, // Chassis ground resistors
        t: circuit.r1,    // Termination resistors 
        i: circuit.r4,    // Input resistors
        bg: circuit.r10,  // Blanking ground
        bi: circuit.r11,  // Blanking inline
        
        // Chassis string
        cs: chassisString, // Add chassis string to share data
        
        // Chroma Vpp
        vpp: chromaVpp, // Add ChromaVpp to share data
        
        // Calculation mode
        mode: calculateMode,
        
        // Blanking resistor values
        cbr: circuit.r208, // Chassis blanking resistor
        cbg: circuit.r207, // Chassis blanking ground resistor
        
        // Capacitor information
        cn: chassisCapacitorNumbers, // Capacitor numbers
        cv: chassisCapacitorValues,  // Capacitor values
        
        // Pin numbers and resistor identifiers
        op: osdPins.join(','), // OSD pin numbers
        jp: junglePins.join(','), // Jungle pin numbers
        ri: chassisResistorIds.join(','), // Chassis resistor identifiers
        gi: chassisGroundResistorIds.join(','), // Chassis ground resistor identifiers
        
        // Toggle states
        rd: showChassisRGBDiodes,         // Chassis RGB diodes
        bd: showChassisBlankingDiode,     // Chassis blanking diode
        id: showMuxBlankingDiode,         // MUX blanking diode
        mt: showMuxTransistor,            // MUX transistor
        cs: showChassisStereo,            // Chassis stereo
        di: showDirectInjection,          // RGB direct injection
        ci: showChassisBlankingInductor,  // Chassis blanking inductor
        bl: showChassisBlankingLine,      // Chassis blanking line
        cl: showChangeCapsLabel,          // Change caps label
        bdp: blankingDiodeBeforeR207,     // Blanking diode position
        bal: showChassisBlankingAddDiodeLabel, // Blanking add diode label
        ral: showChassisRGBAddDiodesLabel, // RGB add diodes label
        
        // Chassis blanking inductor number if available
        bin: circuit.chassis_blanking_inductor_number || null,
        
        // Vpp input voltage
        v: vppInputVoltage,
        
        // Model info
        m: crtModelNumber || null,
        o: osdModelNumber || null,
        j: jungleModelNumber || null,
        
        // Direct injection capacitor values
        dic: directInjectionCaps,
        
        // MUX transistor base resistor
        r12: circuit.r12
      };
      
      // Convert to JSON and encode as base64
      const jsonString = JSON.stringify(shareData);
      const base64Data = btoa(jsonString);
      
      // Create the full URL with base64 encoded data
      const url = `${window.location.origin}${window.location.pathname}?data=${base64Data}`;
      
      // Store the generated URL in state
      setShareableLink(url);
      
      // Copy to clipboard
      navigator.clipboard.writeText(url).then(() => {
        setShareNotification('Shareable link copied to clipboard!');
        setTimeout(() => setShareNotification(''), 3000);
      });
      
      return url;
    } catch (err) {
      console.error('Error generating shareable link:', err);
      setShareNotification('Failed to generate link');
      setTimeout(() => setShareNotification(''), 3000);
      return null;
    }
  };

  // Function to parse URL params and set circuit values
  const loadCircuitFromParams = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      
      // Check if we have base64 encoded data
      if (params.has('data')) {
        try {
          // Decode base64 data
          const base64Data = params.get('data');
          const jsonString = atob(base64Data);
          const shareData = JSON.parse(jsonString);
          
          // Create a new circuit object with current values as defaults
          const newCircuit = {...circuit};
          
          // Load chassis string if available
          if (shareData.cs) {
            setChassisString(shareData.cs);
          }

          // Load ChromaVpp if available
          if (shareData.vpp) {
            setChromaVpp(shareData.vpp);
          }
          
          // Load circuit values from decoded data
          if (shareData.cr !== undefined && !isNaN(parseFloat(shareData.cr))) {
            const value = parseFloat(shareData.cr);
            newCircuit.r201 = value;
            newCircuit.r202 = value;
            newCircuit.r203 = value;
          }
          
          if (shareData.cg !== undefined && !isNaN(parseFloat(shareData.cg))) {
            const value = parseFloat(shareData.cg);
            newCircuit.r204 = value;
            newCircuit.r205 = value;
            newCircuit.r206 = value;
          }
          
          if (shareData.t !== undefined && !isNaN(parseFloat(shareData.t))) {
            const value = parseFloat(shareData.t);
            newCircuit.r1 = value;
            newCircuit.r2 = value;
            newCircuit.r3 = value;
          }
          
          if (shareData.i !== undefined && !isNaN(parseFloat(shareData.i))) {
            const value = parseFloat(shareData.i);
            newCircuit.r4 = value;
            newCircuit.r5 = value;
            newCircuit.r6 = value;
          }
          
          if (shareData.bg !== undefined && !isNaN(parseFloat(shareData.bg))) {
            newCircuit.r10 = parseFloat(shareData.bg);
          }
          
          if (shareData.bi !== undefined && !isNaN(parseFloat(shareData.bi))) {
            newCircuit.r11 = parseFloat(shareData.bi);
          }
          
          if (shareData.cbr !== undefined && !isNaN(parseFloat(shareData.cbr))) {
            newCircuit.r208 = parseFloat(shareData.cbr);
          }
          
          if (shareData.cbg !== undefined && shareData.cbg !== null) {
            if (!isNaN(parseFloat(shareData.cbg))) {
              newCircuit.r207 = parseFloat(shareData.cbg);
            } else {
              newCircuit.r207 = '';
            }
          }
          
          if (shareData.r12 !== undefined && !isNaN(parseFloat(shareData.r12))) {
            newCircuit.r12 = parseFloat(shareData.r12);
          }
          
          if (shareData.bin) {
            newCircuit.chassis_blanking_inductor_number = shareData.bin;
          }
          
          // Apply the updated circuit values
          setCircuit(newCircuit);
          
          // Load capacitor information
          if (shareData.cn) setChassisCapacitorNumbers(shareData.cn);
          if (shareData.cv) setChassisCapacitorValues(shareData.cv);
          
          // Load pin numbers
          if (shareData.op) {
            const pinArray = shareData.op.split(',');
            if (pinArray.length >= 4) {
              setOsdPins([
                pinArray[0].trim(),
                pinArray[1].trim(),
                pinArray[2].trim(),
                pinArray[3].trim()
              ]);
            }
          }
          
          if (shareData.jp) {
            const pinArray = shareData.jp.split(',');
            if (pinArray.length >= 4) {
              setJunglePins([
                pinArray[0].trim(),
                pinArray[1].trim(),
                pinArray[2].trim(),
                pinArray[3].trim()
              ]);
            }
          }
          
          // Load resistor identifiers
          if (shareData.ri) {
            const idArray = shareData.ri.split(',');
            if (idArray.length >= 4) {
              setChassisResistorIds([
                idArray[0].trim(),
                idArray[1].trim(),
                idArray[2].trim(),
                idArray[3].trim()
              ]);
            }
          }
          
          if (shareData.gi) {
            const idArray = shareData.gi.split(',');
            if (idArray.length >= 4) {
              setChassisGroundResistorIds([
                idArray[0].trim(),
                idArray[1].trim(),
                idArray[2].trim(),
                idArray[3].trim()
              ]);
            }
          }
          
          // Set toggle states
          if (shareData.rd !== undefined) setShowChassisRGBDiodes(shareData.rd);
          if (shareData.bd !== undefined) setShowChassisBlankingDiode(shareData.bd);
          if (shareData.id !== undefined) setShowMuxBlankingDiode(shareData.id);
          if (shareData.mt !== undefined) setShowMuxTransistor(shareData.mt);
          if (shareData.cs !== undefined) setShowChassisStereo(shareData.cs);
          if (shareData.di !== undefined) setShowDirectInjection(shareData.di);
          if (shareData.ci !== undefined) setShowChassisBlankingInductor(shareData.ci);
          if (shareData.bl !== undefined) setShowChassisBlankingLine(shareData.bl);
          if (shareData.cl !== undefined) setShowChangeCapsLabel(shareData.cl);
          if (shareData.bdp !== undefined) setBlankingDiodeBeforeR207(shareData.bdp);
          if (shareData.bal !== undefined) setShowChassisBlankingAddDiodeLabel(shareData.bal);
          if (shareData.ral !== undefined) setShowChassisRGBAddDiodesLabel(shareData.ral);
          
          // Set Vpp input voltage
          if (shareData.v !== undefined && !isNaN(parseFloat(shareData.v))) {
            setVppInputVoltage(parseFloat(shareData.v));
          }
          
          // Set model numbers
          if (shareData.m) {
            setCrtModelNumber(shareData.m);
            setSelectedCrtModel({
              value: shareData.m,
              label: shareData.m
            });
          }
          
          if (shareData.o) setOsdModelNumber(shareData.o);
          if (shareData.j) setJungleModelNumber(shareData.j);
          
          // Set calculation mode
          if (shareData.mode && (shareData.mode === 'rgbResistor' || shareData.mode === 'outputVoltage')) {
            setCalculateMode(shareData.mode);
          }
          
          // Load direct injection capacitor values
          if (shareData.dic) {
            setDirectInjectionCaps(shareData.dic);
          }
          
          return; // Exit early since we processed base64 data
        } catch (decodeError) {
          console.error('Error decoding base64 share data:', decodeError);
          // Fall through to legacy URL parameter parsing
        }
      }

      // Check if a CRT slug is provided and load the model using the existing API endpoint (works for both base64 and legacy)
      if (params.has('crt_slug')) {
        const modelSlug = params.get('crt_slug');
        if (modelSlug) {
          // Fetch CRT details for this model
          fetchCrtDetails(modelSlug);
        }
      }
      
      // Legacy URL parameter parsing (for backward compatibility)
      // Create a new circuit object with current values as defaults
      const newCircuit = {...circuit};
      
      // Handle shortened parameter names for resistors
      if (params.has('cr')) {
        const value = parseFloat(params.get('cr'));
        if (!isNaN(value)) {
          newCircuit.r201 = value;
          newCircuit.r202 = value;
          newCircuit.r203 = value;
        }
      }
      
      if (params.has('cg')) {
        const value = parseFloat(params.get('cg'));
        if (!isNaN(value)) {
          newCircuit.r204 = value;
          newCircuit.r205 = value;
          newCircuit.r206 = value;
        }
      }
      
      if (params.has('t')) {
        const value = parseFloat(params.get('t'));
        if (!isNaN(value)) {
          newCircuit.r1 = value;
          newCircuit.r2 = value;
          newCircuit.r3 = value;
        }
      }
      
      if (params.has('i')) {
        const value = parseFloat(params.get('i'));
        if (!isNaN(value)) {
          newCircuit.r4 = value;
          newCircuit.r5 = value;
          newCircuit.r6 = value;
        }
      }
      
      if (params.has('bg')) {
        const value = parseFloat(params.get('bg'));
        if (!isNaN(value)) {
          newCircuit.r10 = value;
        }
      }
      
      if (params.has('bi')) {
        const value = parseFloat(params.get('bi'));
        if (!isNaN(value)) {
          newCircuit.r11 = value;
        }
      }
      
      // Handle explicit blanking resistor values
      if (params.has('cbr')) {
        const value = parseFloat(params.get('cbr'));
        if (!isNaN(value)) {
          newCircuit.r208 = value;
        }
      }
      
      if (params.has('cbg')) {
        const value = parseFloat(params.get('cbg'));
        if (!isNaN(value)) {
          newCircuit.r207 = value;
        } else {
          newCircuit.r207 = '';
        }
      } else {
        newCircuit.r207 = '';
      }
      
      // Apply the updated circuit values
      setCircuit(newCircuit);
      
      // Load capacitor information
      if (params.has('cn')) {
        setChassisCapacitorNumbers(params.get('cn'));
      }
      
      if (params.has('cv')) {
        setChassisCapacitorValues(params.get('cv'));
      }
      
      // Load pin numbers
      if (params.has('op')) {
        const pinStr = params.get('op');
        const pinArray = pinStr.split(',');
        if (pinArray.length >= 4) {
          setOsdPins([
            pinArray[0].trim(),
            pinArray[1].trim(),
            pinArray[2].trim(),
            pinArray[3].trim()
          ]);
        }
      }
      
      if (params.has('jp')) {
        const pinStr = params.get('jp');
        const pinArray = pinStr.split(',');
        if (pinArray.length >= 4) {
          setJunglePins([
            pinArray[0].trim(),
            pinArray[1].trim(),
            pinArray[2].trim(),
            pinArray[3].trim()
          ]);
        }
      }
      
      // Load resistor identifiers
      if (params.has('ri')) {
        const idStr = params.get('ri');
        const idArray = idStr.split(',');
        if (idArray.length >= 4) {
          setChassisResistorIds([
            idArray[0].trim(),
            idArray[1].trim(),
            idArray[2].trim(),
            idArray[3].trim()
          ]);
        }
      }
      
      if (params.has('gi')) {
        const idStr = params.get('gi');
        const idArray = idStr.split(',');
        if (idArray.length >= 4) {
          setChassisGroundResistorIds([
            idArray[0].trim(),
            idArray[1].trim(),
            idArray[2].trim(),
            idArray[3].trim()
          ]);
        }
      }
      
      // Set toggle states from shortened parameter names
      if (params.has('rd')) {
        setShowChassisRGBDiodes(params.get('rd') === '1');
      }
      
      if (params.has('bd')) {
        setShowChassisBlankingDiode(params.get('bd') === '1');
      }
      
      if (params.has('id')) {
        const isEnabled = params.get('id') === '1';
        setShowMuxBlankingDiode(isEnabled);
      }
      
      // Set Vpp input voltage
      if (params.has('v')) {
        const vpp = parseFloat(params.get('v'));
        if (!isNaN(vpp)) {
          setVppInputVoltage(vpp);
        }
      }
      
      // Set model numbers
      if (params.has('m')) {
        setCrtModelNumber(params.get('m'));
        setSelectedCrtModel({
          value: params.get('m'),
          label: params.get('m')
        });
      }
      
      if (params.has('o')) {
        setOsdModelNumber(params.get('o'));
      }
      
      if (params.has('j')) {
        setJungleModelNumber(params.get('j'));
      }
      
      // Set calculation mode
      if (params.has('mode')) {
        const mode = params.get('mode');
        if (mode === 'rgbResistor' || mode === 'outputVoltage') {
          setCalculateMode(mode);
        }
      }
      
      // For chassis stereo, check 'cs' parameter
      if (params.has('cs')) {
        setShowChassisStereo(params.get('cs') === '1');
      }
      
      // For RGB direct injection, check 'di' parameter
      if (params.has('di')) {
        setShowDirectInjection(params.get('di') === '1');
      }
      
      // For chassis blanking inductor, check 'bi' parameter
      if (params.has('bi')) {
        setShowChassisBlankingInductor(params.get('bi') === '1');
      }
      
      // For chassis blanking line, check 'bl' parameter
      if (params.has('bl')) {
        setShowChassisBlankingLine(params.get('bl') === '1');
      }
      
      // For change caps label, check 'cl' parameter
      if (params.has('cl')) {
        setShowChangeCapsLabel(params.get('cl') === '1');
      }
      
      // For blanking diode position, check 'bdp' parameter
      if (params.has('bdp')) {
        setBlankingDiodeBeforeR207(params.get('bdp') === '1');
      }
      
      // For chassis blanking inductor number, check 'bin' parameter
      if (params.has('bin')) {
        setCircuit(prev => ({
          ...prev,
          chassis_blanking_inductor_number: params.get('bin')
        }));
      }
    } catch (err) {
      console.error('Error loading circuit from URL params:', err);
    }
  };

  // Load circuit values from URL params on component mount
  useEffect(() => {
    if (window.location.search) {
      loadCircuitFromParams();
    }
  }, []);

  // Handlers for pin numbers and resistor identifiers
  const handleJunglePinsChange = (e) => {
    const value = e.target.value;
    
    // Update the junglePins array for SVG display
    try {
      const pinArray = value.split(',');
      if (pinArray.length >= 4) {
        setJunglePins([
          pinArray[0].trim(),
          pinArray[1].trim(),
          pinArray[2].trim(),
          pinArray[3].trim()
        ]);
      }
    } catch (error) {
      console.error("Error parsing jungle pins:", error);
    }
  };
  
  const handleOsdPinsChange = (e) => {
    const value = e.target.value;
    
    // Update the osdPins array for SVG display
    try {
      const pinArray = value.split(',');
      if (pinArray.length >= 4) {
        setOsdPins([
          pinArray[0].trim(),
          pinArray[1].trim(),
          pinArray[2].trim(),
          pinArray[3].trim()
        ]);
      }
    } catch (error) {
      console.error("Error parsing OSD pins:", error);
    }
  };
  
  const handleInlineResistorIdsChange = (e) => {
    const value = e.target.value;
    
    // Update the chassisResistorIds array for SVG display
    try {
      const resistorArray = value.split(',');
      if (resistorArray.length >= 4) {
        setChassisResistorIds([
          resistorArray[0].trim(),
          resistorArray[1].trim(),
          resistorArray[2].trim(),
          resistorArray[3].trim()
        ]);
      }
    } catch (error) {
      console.error("Error parsing inline resistor IDs:", error);
    }
  };
  
  const handleGroundResistorIdsChange = (e) => {
    const value = e.target.value;
    
    // Update the chassisGroundResistorIds array for SVG display
    try {
      const resistorArray = value.split(',');
      if (resistorArray.length >= 4) {
        setChassisGroundResistorIds([
          resistorArray[0].trim(),
          resistorArray[1].trim(),
          resistorArray[2].trim(),
          resistorArray[3].trim()
        ]);
      }
    } catch (error) {
      console.error("Error parsing ground resistor IDs:", error);
    }
  };

  // Handle capacitor numbers change
  const handleCapacitorNumbersChange = (e) => {
    setChassisCapacitorNumbers(e.target.value);
  };

  // Handle capacitor values change
  const handleCapacitorValuesChange = (e) => {
    setChassisCapacitorValues(e.target.value);
  };

  const handleChassisBlankingAddDiodeLabelToggle = (e) => {
    setShowChassisBlankingAddDiodeLabel(e.target.checked);
  };

  const handleChassisRGBAddDiodesLabelToggle = (e) => {
    setShowChassisRGBAddDiodesLabel(e.target.checked);
  };

  const handleChassisBlankingInductorToggle = (e) => {
    setShowChassisBlankingInductor(e.target.checked);
  };

  const handleChassisStereoToggle = (e) => {
    setShowChassisStereo(e.target.checked);
  };

  // Render mod boards function similar to CRTDetail.js
  const renderModBoards = () => {
    if (!crtModBoards || crtModBoards.length === 0) return null;
    
    return (
      <div className="mod-boards-section mt-2">
        {crtModBoards.map((board, idx) => {
          return (
            board.purchase_links && board.purchase_links.length > 0 &&
              board.purchase_links.map((link, linkIdx) => (
                <a
                  key={linkIdx}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mod-board-purchase-link btn btn-outline-primary btn-sm me-2 mb-2"
                >
                  <svg width="16" height="16" viewBox="0 0 400 350" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="me-1">
                    <path d="M64 43L81.4154 45.6242C93.0396 47.3758 102.57 55.7552 105.794 67.0597L139.79 186.23C143.465 199.115 155.24 208 168.639 208H275.303C287.513 208 298.505 200.6 303.099 189.288L331.707 118.841C334.1 112.948 333.636 106.28 330.449 100.775V100.775C326.767 94.4157 319.975 90.5 312.627 90.5H146" stroke="currentColor" strokeWidth="20" strokeLinecap="round" fill="none"/>
                    <circle cx="171" cy="271" r="26" fill="currentColor"/>
                    <circle cx="267" cy="271" r="26" fill="currentColor"/>
                    <circle cx="185" cy="149" r="15" fill="currentColor"/>
                    <circle cx="227" cy="149" r="15" fill="currentColor"/>
                    <circle cx="269" cy="149" r="15" fill="currentColor"/>
                  </svg>
                  {board.name}
                </a>
              ))
            )
        })} 
      </div>
    );
  };

  // Calculate meta description content
  const metaDescription = crtModelNumber 
    ? `Calculate RGB MUX circuit values for ${crtModelNumber} CRT monitor. Free tool for designing RGB modification circuits with resistor & voltage calculations.`
    : "Free RGB MUX calculator for CRT monitor RGB modifications. Calculate resistor values, voltage levels, and design RGB multiplexer circuits for any CRT display.";

  // Calculate page title
  const pageTitle = crtModelNumber 
    ? `${crtModelNumber} RGB MUX Calculator | CRT RGB Mod Circuit Design Tool`
    : "RGB MUX Calculator | CRT RGB Modification Circuit Design Tool";

  return (
    <Container>
      <ToastContainer />
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        
        {/* Additional SEO meta tags */}
        <meta name="keywords" content="RGB MUX calculator, CRT RGB modification, RGB circuit design, CRT monitor upgrade, retro gaming, voltage divider calculator, resistor calculator, RGB multiplexer" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Sunthar's Super Sector" />
        <link rel="canonical" href={`${MAIN_URL}/rgb-mux-calculator`} />
        
        {/* Open Graph tags */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${MAIN_URL}/rgb-mux-calculator`} />
        <meta property="og:image" content={`${MAIN_URL}/rgb-mux-calculator-preview.png`} />
        <meta property="og:site_name" content="Sunthar's Super Sector" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={`${MAIN_URL}/rgb-mux-calculator-preview.png`} />
        <meta name="twitter:site" content="@SuntharSector" />
      </Helmet>
      <h1 className="mb-md-4 mb-1">RGB MUX (Multiplexer) Circuit Calculator</h1>
      
      {/* RGB Not Modifiable Warning */}
      {crtRgbModifiable === 'No' && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-danger d-flex align-items-center" role="alert">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" className="bi bi-exclamation-triangle-fill flex-shrink-0 me-3" viewBox="0 0 16 16">
                <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
              </svg>
              <div>
                <strong>CRT Not RGB Modifiable:</strong> The selected CRT model cannot be modified for RGB output. This model does not support RGB modifications due to hardware limitations or design constraints. Using this calculator will not result in a working RGB modification.
              </div>
            </div>
          </Col>
        </Row>
      )}
      
      {/* Login notification for unauthenticated users */}
      {!token && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-info d-flex align-items-center" role="alert">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" className="bi bi-info-circle-fill flex-shrink-0 me-2" viewBox="0 0 16 16">
                <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/>
              </svg>
              <div>
                You can use the RGB mux calculator without logging in, but the values shown are <i>default values</i> only and not specific to any CRT model. 
                <a href="#" onClick={(e) => { e.preventDefault(); handleLogin(); }} className="alert-link ms-2" style={{color: '#ffda6a'}}>Login</a> to access accurate pre-calculated values from our database for specific CRT models.
              </div>
            </div>
          </Col>
        </Row>
      )}      

      {/* Keep the calculator form and results in a separate row structure */}
      <Row className="mt-md-4 mt-2">
        <Col md={8} className="mb-3">
          <Card className="diagram-card">
            <Card.Header as="h5" className="diagram-header">
              <div className="d-flex justify-content-between align-items-center">
                <span>Circuit Diagram {crtModelNumber !== null && crtModelNumber !== '' && `for ${crtModelNumber}`}</span>
                <div className="d-flex align-items-center gap-2">
                  {/* Super user Edit CRT button */}
                  {role === 'super' && selectedCrtModel?.id && (
                    <Button 
                      as="a" 
                      href={`/superadmin/edit-crt/${selectedCrtModel.id}`} 
                      variant="outline-warning" 
                      size="sm"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-pencil-square me-1" viewBox="0 0 16 16">
                        <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                        <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                      </svg>
                      Edit CRT
                    </Button>
                  )}
                  {/* CRT Details button */}
                  {selectedCrtModel?.slug && (
                    <Button 
                      as="a" 
                      href={`/crts/${selectedCrtModel.slug}`} 
                      variant="outline-primary" 
                      size="sm"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-info-circle me-1" viewBox="0 0 16 16">
                        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                        <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
                      </svg>
                      CRT Details
                    </Button>
                  )}
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              {/* Warning message when CRT data is loaded but calculator isn't enabled */}
              {selectedCrtModel?.slug && crtDataLoaded && !calculatorEnabled && (
                <div className="alert alert-warning mb-3" role="alert">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" className="bi bi-exclamation-triangle-fill flex-shrink-0 me-2" viewBox="0 0 16 16">
                    <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                  </svg>
                  <strong>Warning:</strong> The RGB MUX Calculator values for {crtModelNumber} have not been verified. The displayed values are defaults and <em>not correct</em> for this CRT model.
                </div>
              )}

              {authMessage && (
                <Row>
                  <Col>
                    <div className={`alert ${crtRgbModifiable === 'No' ? 'alert-danger' : 'alert-info'}`} role="alert">
                      {authMessage}
                    </div>
                  </Col>
                </Row>
              )}
              
              <div className="circuit-container">
                <div className="circuit-diagram">
                  {renderRGBMuxSVG()}
                </div>
              </div>
              
              <div className="d-flex justify-content-end mt-2 mb-2">
                <Button 
                  variant="outline-primary" 
                  onClick={handleDownloadDiagram}
                  className="d-flex align-items-center me-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-download me-2" viewBox="0 0 16 16">
                    <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                    <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
                  </svg>
                  Download Diagram
                </Button>
                
                <Button 
                  variant="outline-success" 
                  onClick={generateShareableLink}
                  className="d-flex align-items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-share me-2" viewBox="0 0 16 16">
                    <path d="M13.5 1a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zM11 2.5a2.5 2.5 0 1 1 .603 1.628l-6.718 3.12a2.5 2.5 0 0 1 0 1.504l6.718 3.12a2.5 2.5 0 1 1-.488.876l-6.718-3.12a2.5 2.5 0 1 1 0-3.256l6.718-3.12A2.5 2.5 0 0 1 11 2.5zm-8.5 4a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm11 5.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z"/>
                  </svg>
                  Share
                </Button>
              </div>
              
              {/* Show notification when link is copied */}
              {shareNotification && (
                <div className="text-center mb-2">
                  <div className="alert alert-success py-2">{shareNotification}</div>
                </div>
              )}
              
              {/* Display shareable link in input field when available */}
              {shareableLink && (
                <Row className="mb-3">
                  <Col>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        value={shareableLink}
                        readOnly
                        onClick={(e) => e.target.select()}
                        aria-label="Shareable link"
                      />
                      <Button 
                        variant="outline-secondary"
                        onClick={() => {
                          navigator.clipboard.writeText(shareableLink);
                          setShareNotification('Shareable link copied to clipboard!');
                          setTimeout(() => setShareNotification(''), 3000);
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-clipboard" viewBox="0 0 16 16">
                          <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"/>
                          <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"/>
                        </svg>
                      </Button>
                    </InputGroup>
                    <Form.Text className="text-muted">
                      Click in the field to select the entire link for manual copying
                    </Form.Text>
                  </Col>
                </Row>
              )}
              
              <hr className="my-3" />
              <Row>
                <Col md={12} className="mb-3">
                  <Form.Group>
                    <Form.Label><strong>CRT Model</strong></Form.Label>
                    <div className="d-flex">
                      <CreatableSelect
                        className="me-2 flex-grow-1"
                        isClearable
                        isSearchable
                        isLoading={isLoadingModels}
                        options={suggestions}
                        value={selectedCrtModel}
                        onInputChange={(inputValue, { action }) => {
                          if (action === 'input-change') {
                            setModelInput(inputValue);
                            debouncedFetch(inputValue);
                          }
                          if (action === 'menu-close') {
                            setModelInput('');
                          }
                        }}
                        inputValue={modelInput}
                        onChange={handleCrtModelChange}
                        placeholder="Select or enter CRT model..."
                        classNamePrefix="dark-select"
                      />
                      {!token && (
                        <a href="#" onClick={(e) => { e.preventDefault(); handleLogin(); }} className="btn btn-primary">
                          Login
                        </a>
                      )}
                    </div>
                    {!token && (
                      <Form.Text className="text-muted">
                        Login to search for CRT models and automatically load circuit values.
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              {/* Compatible Mod Boards Section - only show for RGB moddable CRTs */}
              {crtRgbModifiable === 'Yes' && crtModBoards && crtModBoards.length > 0 && (
                <Row>
                  <Col>
                    <div className="alert alert-success d-flex align-items-start" role="alert">
                      <div>
                        <div className="alert-heading h6 mb-1">Compatible Mod Kit Available</div>
                        <div className="small text-muted mb-2">
                          Mod kit is available for the {crtModelNumber}. Purchase this kit to easily implement your RGB modification.
                        </div>
                        {renderModBoards()}
                      </div>
                    </div>
                  </Col>
                </Row>
              )}

              {/* RGB Modification Notes Section - only show for RGB moddable CRTs with notes from authenticated endpoint */}
              {crtRgbModifiable === 'Yes' && crtDataLoaded && selectedCrtModel?.slug && crtRgbModNotes && token && (
                <Row>
                  <Col>
                    <div className="alert alert-info d-flex align-items-start" role="alert">
                      <div>
                        <div className="alert-heading h6 mb-2">RGB Modification Notes for {crtModelNumber}</div>
                        <div className="small">
                          <ReactMarkdown 
                            components={{
                              p: ({children}) => <p className="mb-2">{children}</p>,
                              ul: ({children}) => <ul className="mb-2 ps-3">{children}</ul>,
                              ol: ({children}) => <ol className="mb-2 ps-3">{children}</ol>,
                              li: ({children}) => <li className="mb-1">{children}</li>,
                              strong: ({children}) => <strong className="fw-bold">{children}</strong>,
                              em: ({children}) => <em className="fst-italic">{children}</em>,
                              code: ({children}) => <code className="bg-light px-1 rounded">{children}</code>,
                              blockquote: ({children}) => <blockquote className="border-start border-3 ps-3 mb-2 text-muted">{children}</blockquote>
                            }}
                          >
                            {crtRgbModNotes}
                          </ReactMarkdown>
                          {crtRgbModLink && (
                            <div className="mt-3 pt-2">
                              <a 
                                href={crtRgbModLink} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="btn btn-outline-primary btn-sm"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-link-45deg me-1" viewBox="0 0 16 16">
                                  <path d="M4.715 6.542 3.343 7.914a3 3 0 1 0 4.243 4.243l1.828-1.829A3 3 0 0 0 8.586 5.5L8 6.086a1.002 1.002 0 0 0-.154.199 2 2 0 0 1 .861 3.337L6.88 11.45a2 2 0 1 1-2.83-2.83l.793-.792a4.018 4.018 0 0 1-.128-1.287z"/>
                                  <path d="M6.586 4.672A3 3 0 0 0 7.414 9.5l.775-.776a2 2 0 0 1-.896-3.346L9.12 3.55a2 2 0 1 1 2.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 1 0-4.243-4.243L6.586 4.672z"/>
                                </svg>
                                View RGB Modification Guide
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              )}

              {/* Display authentication message if needed */}
              {/*authMessage && (
                <Row>
                  <Col>
                    <div className={`alert ${crtRgbModifiable === 'No' ? 'alert-danger' : 'alert-info'}`} role="alert">
                      {authMessage}
                    </div>
                  </Col>
                </Row>
              )*/}

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>OSD Chip Model Number</strong></Form.Label>
                    <InputGroup>
                      <InputGroup.Text>OSD</InputGroup.Text>
                      <Form.Control 
                        type="text" 
                        value={osdModelNumber}
                        onChange={handleOsdModelNumberChange}
                        placeholder="Enter OSD model number"
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Jungle Chip Model Number</strong></Form.Label>
                    <InputGroup>
                      <InputGroup.Text>Jungle</InputGroup.Text>
                      <Form.Control 
                        type="text" 
                        value={jungleModelNumber}
                        onChange={handleJungleModelNumberChange}
                        placeholder="Enter jungle model number"
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              {/* Advanced configuration toggle */}
              <div className="d-flex justify-content-center align-items-center mb-3">
                <Button 
                  variant="outline-secondary" 
                  onClick={() => setAdvancedConfigCollapsed(!advancedConfigCollapsed)}
                  className="d-flex align-items-center"
                  size="sm"
                >
                  {advancedConfigCollapsed ? (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-chevron-down me-2" viewBox="0 0 16 16">
                        <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
                      </svg>
                      Show Advanced Configuration
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-chevron-up me-2" viewBox="0 0 16 16">
                        <path fillRule="evenodd" d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708l6-6z"/>
                      </svg>
                      Hide Advanced Configuration
                    </>
                  )}
                </Button>
              </div>

              {/* Additional pin numbers and resistor identifiers - collapsible section */}
              <div className={`additional-config-section ${advancedConfigCollapsed ? 'd-none' : ''}`}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>OSD Pin Numbers (R,G,B,BL)</Form.Label>
                      <Form.Control 
                        type="text" 
                        value={osdPins.join(',')}
                        onChange={handleOsdPinsChange}
                        placeholder="Enter OSD pin numbers (e.g., 19,20,21,22)"
                      />
                      <Form.Text className="text-muted">
                        Comma-separated: Red, Green, Blue, Blanking
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Jungle Pin Numbers (R,G,B,BL)</Form.Label>
                      <Form.Control 
                        type="text" 
                        value={junglePins.join(',')}
                        onChange={handleJunglePinsChange}
                        placeholder="Enter Jungle pin numbers (e.g., 12,13,14,15)"
                      />
                      <Form.Text className="text-muted">
                        Comma-separated: Red, Green, Blue, Blanking
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Inline Resistor IDs</Form.Label>
                      <Form.Control 
                        type="text" 
                        value={chassisResistorIds.join(',')}
                        onChange={handleInlineResistorIdsChange}
                        placeholder="Enter inline resistor IDs (e.g., R849,R848,R847,R846)"
                      />
                      <Form.Text className="text-muted">
                        Comma-separated resistor identifiers
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Ground Resistor IDs</Form.Label>
                      <Form.Control 
                        type="text" 
                        value={chassisGroundResistorIds.join(',')}
                        onChange={handleGroundResistorIdsChange}
                        placeholder="Enter ground resistor IDs (e.g., R844,R843,R842)"
                      />
                      <Form.Text className="text-muted">
                        Comma-separated resistor identifiers
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                
                {/* Capacitor inputs */}
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Chassis Capacitor IDs</Form.Label>
                      <Form.Control 
                        type="text" 
                        value={chassisCapacitorNumbers}
                        onChange={handleCapacitorNumbersChange}
                        placeholder="Enter capacitor numbers (e.g., C1, C2, C3)"
                      />
                      <Form.Text className="text-muted">
                        Comma-separated capacitor identifiers
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Chassis Capacitor Values (μF)</Form.Label>
                      <Form.Control 
                        type="text" 
                        value={chassisCapacitorValues}
                        onChange={handleCapacitorValuesChange}
                        placeholder="Enter capacitor values (e.g., 0.1)"
                      />
                      <Form.Text className="text-muted">
                        Values in microfarads (μF)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                
                {/* Toggle controls row */}
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <div className="mt-2">
                        <Form.Check 
                          type="switch"
                          id="change-caps-label-toggle"
                          label={`Change Caps Label? ${showChangeCapsLabel ? 'Show' : 'Hide'}`}
                          checked={showChangeCapsLabel}
                          onChange={handleChangeCapsLabelToggle}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <div className="mt-2">
                        <Form.Check 
                          type="switch"
                          id="stereo-section-toggle"
                          label={`Stereo? ${showChassisStereo ? 'Enabled' : 'Disabled (Mono)'}`}
                          checked={showChassisStereo}
                          onChange={handleChassisStereoToggle}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  {/* Blanking Diode Position toggle - only show when chassis blanking diode is enabled */}
                  <Col md={6}>
                    {/* Diode Needs Adding toggle - only show when chassis blanking diode is enabled and positioned before ground */}
                    <Form.Group className="mb-3">
                      <div className="mt-2">
                        <Form.Check 
                          type="switch"
                          id="diode-needs-adding-toggle"
                          label={`Blanking diode added? ${(showChassisBlankingAddDiodeLabel) ? 'Yes' : (showChassisBlankingDiode) ? 'Existing diode' : 'No'}`}
                          checked={showChassisBlankingAddDiodeLabel}
                          onChange={handleChassisBlankingAddDiodeLabelToggle}
                          disabled={!showChassisBlankingDiode}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    {/* RGB Diodes Needs Adding toggle - only show when chassis RGB diodes are enabled */}
                    <Form.Group className="mb-3">
                      <div className="mt-2">
                        <Form.Check 
                          type="switch"
                          id="rgb-diodes-needs-adding-toggle"
                          label={`RGB diodes added? ${showChassisRGBAddDiodesLabel ? 'Yes' : (showChassisRGBDiodes) ? 'Existing diodes' : 'No'}`}
                          checked={showChassisRGBAddDiodesLabel}
                          onChange={handleChassisRGBAddDiodesLabelToggle}
                          disabled={!showChassisRGBDiodes}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  {/* MUX Transistor toggle */}
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <div className="mt-2">
                        <Form.Check 
                          type="switch"
                          id="mux-transistor-toggle"
                          label={`Luma MUX Transistor? ${showMuxTransistor ? 'Enabled' : 'Disabled'}`}
                          checked={showMuxTransistor}
                          onChange={handleMuxTransistorToggle}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                  {/* Chassis Blanking Inductor toggle */}
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <div className="mt-2">
                        <Form.Check 
                          type="switch"
                          id="chassis-blanking-inductor-toggle"
                          label={`Chassis Blanking Inductor? ${showChassisBlankingInductor ? 'Show' : 'Hide'}`}
                          checked={showChassisBlankingInductor}
                          onChange={handleChassisBlankingInductorToggle}
                        />
                      </div>
                    </Form.Group>
                    {showChassisBlankingInductor && (
                    <Form.Group className="mb-3">
                      <Form.Label>Blanking Inductor Number</Form.Label>
                      <Form.Control 
                        type="text" 
                        value={circuit.chassis_blanking_inductor_number || ''}
                        onChange={(e) => setCircuit(prev => ({ 
                          ...prev, 
                          chassis_blanking_inductor_number: e.target.value 
                        }))}
                        placeholder="LN003"
                        disabled={!showChassisBlankingInductor}
                      />
                      <Form.Text className="text-muted">
                        Inductor part number or identifier (e.g., LN003)
                      </Form.Text>
                    </Form.Group>
                    )}
                  </Col>
                </Row>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} className="mb-3">
          <Card className="form-card">
            <Card.Header as="h5">
              <Row>
                <Col>Configuration</Col>
              </Row>
            </Card.Header>
            <Card.Body className="form-card-body">
              <Form className="calculator-form">
                <div className="calculator-controls">

                  {/* Direct Injection toggle switch */}
                  <Row className="mb-3">
                    <Col xs={12}>
                      <Form.Group className="mb-0">
                        <div className="direct-injection-toggle-input">
                          <Form.Check 
                            type="switch"
                            id="direct-injection-toggle"
                            label={`Direct Injection? ${showDirectInjection ? 'Enabled' : 'Disabled'}`}
                            checked={showDirectInjection}
                            onChange={handleDirectInjectionToggle}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  {/* Chassis Blanking Line toggle - only show when Direct Injection is enabled */}
                  {showDirectInjection && (
                    <Row className="mb-3">
                      <Col xs={12}>
                        <Form.Group className="mb-0">
                          <div className="chassis-blanking-line-toggle-input">
                            <Form.Check 
                              type="switch"
                              id="chassis-blanking-line-toggle"
                              label={`Chassis Blanking Line? ${showChassisBlankingLine ? 'Enabled' : 'Disabled'}`}
                              checked={showChassisBlankingLine}
                              onChange={handleChassisBlankingLineToggle}
                            />
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                  )}
                  
                  {/* Diode toggle switch */}
                  <Row className="mb-3">
                    <Col xs={12}>
                      <Form.Group className="mb-0">
                        <div className="chassis-rgb-diodes-toggle-input">
                          <Form.Check 
                            type="switch"
                            id="chassis-rgb-diodes-toggle"
                            label={`Chassis RGB Diodes? ${showDirectInjection ? 'Disabled' : (showChassisRGBDiodes ? 'Added' : 'Not Added')}`}
                            checked={showChassisRGBDiodes}
                            disabled={showDirectInjection}
                            onChange={handleChassisRGBDiodesToggle}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Diode toggle switch */}
                  <Row className="mb-3">
                    <Col xs={12}>
                      <Form.Group className="mb-0">
                        <div className="chassis-blanking-diode-toggle-input">
                          <Form.Check 
                            type="switch"
                            id="chassis-blanking-diode-toggle"
                            label={`Chassis Blanking Diode? ${showChassisBlankingDiode ? 'Added' : 'Not Added'}`}
                            checked={showChassisBlankingDiode}
                            onChange={handleChassisBlankingDiodeToggle}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>

                  {showChassisBlankingDiode && (
                  <Row className="mb-3">
                    <Col xs={12}>
                      <Form.Group className="mb-0">
                        <div className="blanking-diode-position-toggle-input">
                          <Form.Check 
                            type="switch"
                            id="blanking-diode-position-toggle"
                            label={`${blankingDiodeBeforeR207 ? 'Diode Position: Before Chassis Ground' : 'Diode Position: After Chassis Ground'}`}
                            checked={blankingDiodeBeforeR207}
                            onChange={handleBlankingDiodePositionToggle}
                            disabled={!showChassisBlankingDiode}
                          />  
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                  )}

                  {/* MUX Blanking Diode Toggle */}
                  <Row className="mb-3">
                    <Col xs={12}>
                      <Form.Group className="mb-0">
                        <div className="mux-blanking-diode-toggle-input">
                          <Form.Check 
                            type="switch"
                            id="mux-blanking-diode-toggle"
                            label={`MUX Blanking Diode (D9/R9)? ${showMuxBlankingDiode ? '1N4148' : 'Shorted'}`}
                            checked={showMuxBlankingDiode}
                            onChange={handleMuxBlankingDiodeToggle}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>

                  {!showDirectInjection && (
                    <>
                      <h5 className="mt-3">Chassis RGB values
                        <InfoCircle 
                          className="ms-2 text-primary" 
                          size={16} 
                          style={{ cursor: 'pointer' }} 
                          onClick={() => setVoltageCardCollapsed(!voltageCardCollapsed)}
                          title="Toggle voltage calculation"
                        />
                      </h5>

                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Inline Resistors (Ω)</Form.Label>
                            <Form.Control 
                              type="number" 
                              name="inputR201R202R203" 
                              value={circuit.r201}
                              onChange={handleInputChange}
                            />
                            <Form.Text className="text-muted">
                              {chassisResistorIds.slice(0, 3).join(', ')}
                            </Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Ground Resistors (Ω)</Form.Label>
                            <Form.Control 
                              type="number" 
                              name="inputR204R205R206" 
                              value={circuit.r204}
                              onChange={handleInputChange}
                            />
                            <Form.Text className="text-muted">
                              {chassisGroundResistorIds.slice(0, 3).join(', ')}
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>
                  
                      {/* Voltage Division Calculation */}
                      <Row className={`mb-3 mt-2 collapse ${voltageCardCollapsed ? '' : 'show'}`}>
                        <Col xs={12}>
                          <Card className="voltage-calc-card">
                            <Card.Body>
                              <h6>RGB/OSD Voltage Division Calculation</h6>
                              <p className="mb-1">
                                <strong>Input Voltage:</strong> 5V
                              </p>
                              <p className="mb-1">
                                <strong>Inline Resistor (R1):</strong> {circuit.r201}Ω
                              </p>
                              <p className="mb-1">
                                <strong>Ground Resistor (R2):</strong> {circuit.r204}Ω
                              </p>
                              <p className="mb-1">
                                <strong>Output Voltage:</strong> {(5 * (circuit.r204 / (circuit.r201 + circuit.r204))).toFixed(2)}V
                              </p>
                              <Form.Text className="text-muted">
                                Formula: Vout = Vin × (R2 / (R1 + R2))
                              </Form.Text>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </>
                  )}

                  {/* On chassis blanking resistor values */}
                  <h5 className="mt-3">Chassis blanking values</h5>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Blanking Resistor (Ω)</Form.Label>
                        <Form.Control 
                          type="number" 
                          name="r208" 
                          value={circuit.r208}
                          onChange={handleInputChange}
                        />
                        <Form.Text className="text-muted">
                          {chassisResistorIds[3]} on chassis
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Blanking Ground (Ω)</Form.Label>
                        <Form.Control 
                          type="number" 
                          name="r207" 
                          value={circuit.r207}
                          onChange={handleInputChange}
                        />
                        <Form.Text className="text-muted">
                          {chassisGroundResistorIds[3] ? chassisGroundResistorIds[3] + ' on chassis' : ''} 
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <h5 className="mt-3">MUX RGB values 
                    <InfoCircle 
                      className="ms-2 text-primary" 
                      size={16} 
                      style={{ cursor: 'pointer' }} 
                      onClick={() => setFormulaCardCollapsed(!formulaCardCollapsed)}
                      title="Toggle formula details"
                    />
                  </h5>
                    
                  {!showDirectInjection && (
                    <>
                      <Row className="mb-3">
                        <Col xs={12}>
                          <Form.Group>
                            <Form.Label>Calculation Mode</Form.Label>
                            <div>
                              <Form.Check
                                type="radio"
                                inline
                                label="Calculate RGB Input Resistance"
                                name="calculationMode"
                                id="calcModeResistance"
                                value="rgbResistor"
                                checked={calculateMode === 'rgbResistor'}
                                onChange={handleCalculateModeChange}
                              />
                              <Form.Check
                                type="radio"
                                inline
                                label="Calculate Output Voltage"
                                name="calculationMode"
                                id="calcModeVoltage"
                                value="outputVoltage"
                                checked={calculateMode === 'outputVoltage'}
                                onChange={handleCalculateModeChange}
                              />
                            </div>
                          </Form.Group>
                        </Col>
                      </Row>
                    
                      {/* Vpp Input Voltage Dropdown */}
                      <Row className="mb-3">
                        <Col xs={12}>
                          <Form.Group>
                            <Form.Label>Analog Vpp (Chroma Peak-to-Peak)</Form.Label>
                            {calculateMode === 'rgbResistor' ? (
                              <Form.Select 
                                value={vppInputVoltage}
                                onChange={handleVppInputVoltageChange}
                              >
                                <option value="0.5">0.5V</option>
                                <option value="0.7">0.7V</option>
                                <option value="1.0">1.0V</option>
                              </Form.Select>
                            ) : (
                              <Form.Control
                                type="text"
                                value={vppInputVoltage}
                                onChange={handleVppInputVoltageChange}
                                className="highlighted-calc-field"
                                disabled
                                placeholder="Calculated output"
                              />
                            )}
                            <Form.Text className="text-muted">
                              {calculateMode === 'rgbResistor' 
                                ? "The peak-to-peak voltage of the analog RGB input signal"
                                : "Calculated output voltage based on resistor values"}
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>
                    </>
                  )}
                  
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Termination (Ω)</Form.Label>
                        <Form.Control 
                          type="number" 
                          name="inputR1R2R3" 
                          value={circuit.r1}
                          onChange={handleInputChange}
                        />
                        <Form.Text className="text-muted">
                          R1, R2, R3 on mux board. Typically termination is 75Ω.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                      {showDirectInjection ? (
                        <>
                          <Form.Label>RGB Input (μF)</Form.Label>
                          <Form.Control 
                            type="number" 
                            step="0.01"
                            name="inputR4R5R6" 
                            value={directInjectionCaps.c4}
                            onChange={handleInputChange}
                          />
                          <Form.Text className="text-muted">
                            Use capacitors on R4, R5, R6 for direct injection. Typically 0.1μF
                          </Form.Text>
                        </>
                      ) : (
                        <>
                          <Form.Label>
                            RGB Input (Ω)
                          </Form.Label>
                          {calculateMode === 'rgbResistor' ? (
                            <Form.Control 
                              type="number" 
                              name="inputR4R5R6" 
                              value={circuit.r4}
                              className="highlighted-calc-field"
                              disabled={true}
                            />
                          ) : (
                            <Form.Select
                              name="inputR4R5R6"
                              value={circuit.r4}
                              onChange={(e) => {
                                const value = parseInt(e.target.value, 10);
                                setCircuit(prev => ({ 
                                  ...prev, 
                                  r4: value,
                                  r5: value,
                                  r6: value
                                }));
                              }}
                            >
                              <option value="120">120Ω</option>
                              <option value="150">150Ω</option>
                              <option value="180">180Ω</option>
                              <option value="220">220Ω</option>
                              <option value="270">270Ω</option>
                              <option value="330">330Ω</option>
                              <option value="360">360Ω</option>
                              <option value="390">390Ω</option>
                              <option value="470">470Ω</option>
                              <option value="560">560Ω</option>
                              <option value="680">680Ω</option>
                              <option value="750">750Ω</option>
                              <option value="820">820Ω</option>
                              <option value="1000">1000Ω</option>
                              <option value="1200">1200Ω</option>
                              <option value="1500">1500Ω</option>
                              <option value="1800">1800Ω</option>
                              <option value="2200">2200Ω</option>
                              <option value="2700">2700Ω</option>
                              <option value="3300">3300Ω</option>
                            </Form.Select>
                          )}
                          <Form.Text className="text-muted">
                            {calculateMode === 'rgbResistor' 
                              ? "R4, R5, R6 on mux board are calculated."
                              : "Select common resistor value for R4, R5, R6"}
                          </Form.Text>
                        </>
                      )}
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  {/* Formula explanation card */}
                  <Row className={`mb-3 collapse ${formulaCardCollapsed ? '' : 'show'}`}>
                    <Col xs={12}>
                      <Card className="formula-explanation-card">
                        <Card.Body>
                          <h6 className="mb-3">
                            {calculateMode === 'rgbResistor' 
                              ? 'RGB Resistor Calculation' 
                              : 'Output Voltage Calculation'}
                          </h6>
                          <p className="mb-1">
                            <strong>V<sub>chroma.peak</sub>:</strong> {vppInputVoltage}V
                          </p>
                          <p className="mb-1">
                            <strong>R<sub>osd.inline.resistor</sub>:</strong> {circuit.r201}Ω
                          </p>
                          <p className="mb-1">
                            <strong>R<sub>termination</sub>:</strong> {circuit.r1}Ω
                          </p>
                          {calculateMode === 'outputVoltage' && (
                            <p className="mb-1">
                              <strong>R<sub>rgb.input</sub>:</strong> {circuit.r4}Ω
                            </p>
                          )}
                          <p className="mb-1">
                            <strong>V<sub>osd</sub>:</strong> 5.0V
                          </p>
                          <p className="mb-1">
                            <strong>Chassis RGB Diodes:</strong> {showChassisRGBDiodes ? '0.7V' : '0V'} {showChassisRGBDiodes ? '(Enabled)' : '(Disabled)'}
                          </p>
                          <p className="mb-1">
                            <strong>Chassis Blanking Diode:</strong> {showChassisBlankingDiode ? '0.7V' : '0V'} {showChassisBlankingDiode ? '(Enabled)' : '(Disabled)'}
                          </p>
                          <p className="mb-1">
                            <strong>MUX Blanking Diode (R9):</strong> {showMuxBlankingDiode ? '0.7V' : '0V'} {showMuxBlankingDiode ? '(Enabled)' : '(Disabled)'}
                          </p>
                          {calculateMode === 'rgbResistor' ? (
                            <p className="mb-1">
                              <strong>Calculated R<sub>rgb.external.resistor</sub>:</strong> {circuit.r4}Ω
                            </p>
                          ) : (
                            <p className="mb-1">
                              <strong>Calculated Output Voltage:</strong> {vppInputVoltage}V
                            </p>
                          )}
                          {calculateMode === 'rgbResistor' ? (
                            <Form.Text className="text-muted">
                              Formula: R<sub>rgb</sub> = (V<sub>peak</sub> × (R<sub>inline</sub> + R<sub>term</sub>) - R<sub>term</sub> × (V<sub>osd</sub> - V<sub>diode</sub>)) / (V<sub>osd</sub> - V<sub>diode</sub> - V<sub>peak</sub>)
                            </Form.Text>
                          ) : (
                            <Form.Text className="text-muted">
                              Formula: V<sub>out</sub> = (V<sub>osd</sub> - V<sub>diode</sub>) × (R<sub>rgb</sub> + R<sub>term</sub>) / (R<sub>rgb</sub> + R<sub>inline</sub> + R<sub>term</sub>)
                            </Form.Text>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                  
                  <h5 className="mt-3">MUX blanking values
                    <InfoCircle 
                      className="ms-2 text-primary" 
                      size={16} 
                      style={{ cursor: 'pointer' }} 
                      onClick={() => setVoltageCardCollapsed(!voltageCardCollapsed)}
                      title="Toggle voltage calculation"
                    />
                    <a 
                      onClick={() => setShowVoltageDividerModal(true)}
                      className="ms-2"
                      style={{ cursor: 'pointer' }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-calculator me-1" viewBox="0 0 16 16">
                        <path d="M12 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h8zM4 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H4z"/>
                        <path d="M4 2.5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5h-7a.5.5 0 0 1-.5-.5v-2zm0 4a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm3-6a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm3-6a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v4a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-4z"/>
                      </svg>
                    </a>
                  </h5>
                  <Row>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Blanking Ground (Ω)</Form.Label>
                        <Form.Control 
                          type="number" 
                          name="r10" 
                          value={circuit.r10}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                      <Form.Text className="text-muted">
                        R10 on mux board.
                      </Form.Text>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Blanking Inline (Ω)</Form.Label>
                        <Form.Control 
                          type="number" 
                          name="r11" 
                          value={circuit.r11}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                      <Form.Text className="text-muted">
                        R11 on mux board.
                      </Form.Text>
                    </Col>
                  </Row>
                  
                  {/* Voltage Division Calculation */}
                  <Row className={`mb-3 mt-2 collapse ${voltageCardCollapsed ? '' : 'show'}`}>
                    <Col xs={12}>
                      <Card className="voltage-calc-card">
                        <Card.Body>
                          <h6>Chassis blanking voltage calculation</h6>
                          <p className="mb-1">
                            <strong>Input Voltage:</strong> 5V
                          </p>
                          <p className="mb-1">
                            <strong>Blanking Resistor (R11):</strong> {circuit.r11}Ω
                          </p>
                          <p className="mb-1">
                            <strong>Blanking Ground Resistor (R207):</strong> {circuit.r207}Ω
                          </p>
                          {showMuxBlankingDiode && (
                            <p className="mb-1">
                              <strong>MUX Blanking Diode (D9) Drop:</strong> 0.7V
                            </p>
                          )}
                          <p className="mb-1">
                            <strong>Output Voltage:</strong> {showMuxBlankingDiode 
                              ? ((5 * (circuit.r207 / (circuit.r11 + circuit.r207))) - 0.7).toFixed(2) 
                              : (5 * (circuit.r207 / (circuit.r11 + circuit.r207))).toFixed(2)}V
                          </p>
                          <Form.Text className="text-muted">
                            Formula: Vout = {showMuxBlankingDiode 
                              ? '(Vin × (R207 / (R11 + R207))) - 0.7V' 
                              : 'Vin × (R207 / (R11 + R207))'}
                          </Form.Text>

                          <Row>
                            <Col md={12} className="mt-3 text-center">
                              <button 
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => setShowVoltageDividerModal(true)}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-calculator me-1" viewBox="0 0 16 16">
                                  <path d="M12 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h8zM4 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H4z"/>
                                  <path d="M4 2.5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5h-7a.5.5 0 0 1-.5-.5v-2zm0 4a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm3-6a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm3-6a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v4a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-4z"/>
                                </svg>
                                Voltage Divider Calculator
                              </button>
                            </Col>
                          </Row>

                          {/* Voltage Divider Calculator Modal */}
                          <Modal 
                            show={showVoltageDividerModal} 
                            onHide={() => setShowVoltageDividerModal(false)}
                            size="xl"
                            centered
                          >
                            <Modal.Header closeButton>
                              <Modal.Title>Voltage Divider Calculator</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                              <VoltageDividerCalculatorCore
                                inputVoltage={5}
                                inlineResistor={parseInt(circuit.r11) || 3300}
                                groundResistor={parseInt(circuit.r207) || parseInt(circuit.r10) || 4500}
                              />
                            </Modal.Body>
                          </Modal>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                  
                  <h5 className="mt-3">MUX audio {showMuxTransistor && 'and transistor'} values</h5>
                  {/* Audio resistors and transistor base resistor row */}
                  <Row>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Audio Resistors (Ω)</Form.Label>
                        <Form.Control 
                          type="number" 
                          name="inputR7R8" 
                          value={circuit.r7}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                      <Form.Text className="text-muted">
                        R7, R8 on mux board.
                      </Form.Text>
                    </Col>
                    {showMuxTransistor && (
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Transistor Resistor (Ω)</Form.Label>
                          <Form.Control 
                            type="number" 
                            name="r12" 
                            value={circuit.r12}
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                        <Form.Text className="text-muted">
                          R12 on mux board. Base resistor for MUX transistor.
                        </Form.Text>
                      </Col>
                    )}
                  </Row>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default RGBMuxCalculator; 